import { useCallback, useMemo, useState, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Link } from "react-router-dom";
import { Button } from "@akuna/ui";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@akuna/ui";
import { DateFieldPopover } from "@/components/ui/date-field-popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AlertTriangle, Check, PlusCircle, Trash2, X, CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { getItemTypeBadgeClass } from "@/lib/item-type-badge";
import {
  DEFAULT_APPOINTMENT_DRIVER_TYPE,
  getEffectiveAppointmentDriverType,
  type AppointmentDriverType,
} from "@/lib/utils/appointment-driver";
import {
  maskDateInput,
  convertDisplayToISO,
  formatISOToDisplay,
  getDateErrorMessage,
  formatPetAge,
} from "@/lib/date-utils";
import { useMaskedDigitsCaret } from "@/hooks/useMaskedDigitsCaret";
import { useModuleAccess } from "@/modules/useModuleAccess";
import {
  useCreateAppointment,
  useDeleteAppointment,
  useUpdateAppointment,
  useVeterinarians,
  BOARDING_APPOINTMENT_TYPES,
  type AppointmentWithDetails,
  type AppointmentInput,
  type AppointmentStatus,
  type GroomingStatus,
  type AppointmentTypeBehavior,
} from "@/hooks/useAppointments";
import { useCreateRecurringAppointments } from "@/hooks/useRecurrence";
import { useVisibleClinics } from "@/hooks/useVisibleClinics";
import { usePetSummary, usePetsByOwner, type PetWithOwner } from "@/hooks/usePets";
import { usePatientSearchForQuickStart } from "@/hooks/usePatientSearchForQuickStart";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { type OwnerWithContacts, useOwner } from "@/hooks/useOwners";
import { useAppointmentStatuses } from "@/hooks/useAppointmentStatuses";
import { useItems, type Item } from "@/hooks/useItems";
import { useAppointmentItems, useReplaceAppointmentItems, type AppointmentItemInput } from "@/hooks/useAppointmentItems";
import { useAppointmentBelongings } from "@/hooks/useAppointmentBelongings";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@akuna/ui";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { Autocomplete, type AutocompleteOption } from "@/components/ui/autocomplete";
import { AppointmentStatusSidePanel } from "@/components/calendar/AppointmentStatusSidePanel";
import { AppointmentPackageSection, type AppointmentPackageDraft } from "@/components/calendar/AppointmentPackageSection";
import { QuickRegisterPatientDialog } from "@/components/calendar/QuickRegisterPatientDialog";
import {
  coerceAppointmentStatusForNonGrooming,
  resolveGroomingAppointmentStatus,
} from "@/lib/utils/grooming-status";
import {
  getFirstActiveStatusName,
  getStatusConfigByName,
} from "@/lib/utils/clinic-appointment-status";
import { summarizeAppointmentBelongingsChecklist } from "@/lib/utils/appointment-belongings";
import {
  formatRecurrenceSummary,
  generateRecurrenceOccurrences,
  type RecurrenceFrequency,
} from "@/lib/recurrence";
import { BEHAVIOR_BANHO_TOSA } from "@/lib/appointments/type";
import { supabase } from "@/integrations/supabase/client";
import { isFinancialSettled } from "@/lib/utils/appointment-financial";
import {
  useAppointmentPatientPackages,
  useCommitAppointmentPatientPackages,
  useReplaceAppointmentPatientPackages,
} from "@/hooks/useAppointmentPatientPackage";
import {
  canCreateFutureAppointmentForPet,
  isPetDeceased,
  shouldBlockRemindersForPet,
} from "@/lib/utils/pet-death";
import { isItemAllowedForBehavior } from "@/lib/appointments/availability";
import { useClinicAppointmentTypes } from "@/hooks/useClinicAppointmentTypes";
import { renderIconBySlug } from "@/lib/event-icons";
import { addMinutesToTimeString, resolveDefaultDurationMinutes } from "@/lib/appointments/default-duration";
import { useClinicDefaultAppointmentDurations } from "@/hooks/useClinicDefaultAppointmentDurations";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";

const appointmentStatusSchema = z.string().trim().min(1, "Status é obrigatório");

const groomingStatusSchema = z.string().trim().min(1, "Status é obrigatório");

const baseAppointmentSchema = z.object({
  clinic_id: z.string({ required_error: "Clínica é obrigatória" }).min(1, "Clínica é obrigatória"),
  vet_user_id: z
    .union([
      z.string().uuid("Selecione um profissional válido"),
      z.literal(""),
    ])
    .optional(),
  owner_id: z.string({ required_error: "Responsável é obrigatório" }).min(1, "Responsável é obrigatório"),
  pet_ids: z
    .array(z.string().min(1))
    .min(1, "Selecione ao menos um paciente"),
  type: z.string().min(1, "Selecione um tipo de agendamento"),
  date: z.date({ required_error: "Data é obrigatória" }),
  start_time: z.string({ required_error: "Horário de início é obrigatório" }).min(1, "Horário de início é obrigatório"),
  end_time: z.string({ required_error: "Horário de término é obrigatório" }).min(1, "Horário de término é obrigatório"),
  notes: z.string().optional(),
  is_taxi_dog: z.boolean().optional(),
  taxi_dog_type: z.enum(["pickup_and_dropoff", "pickup", "dropoff"]).nullable().optional(),
});

const appointmentFormSchema = baseAppointmentSchema.extend({
  status: appointmentStatusSchema.optional(),
  grooming_status: groomingStatusSchema.optional(),
  is_recurring: z.boolean().optional(),
  recurrence_frequency: z.enum(["weekly", "monthly", "yearly"]).optional(),
  recurrence_interval: z.number().int().min(1).nullable().optional(),
  recurrence_weekdays: z.array(z.number().int().min(0).max(6)).optional(),
  recurrence_end_type: z.enum(["none", "date", "count"]).optional(),
  recurrence_ends_on: z.date().optional(),
  recurrence_max_occurrences: z.number().int().min(1).optional(),
}).superRefine((data, ctx) => {
  if (data.is_recurring && data.recurrence_interval == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Intervalo é obrigatório",
      path: ["recurrence_interval"],
    });
  }
});

type AppointmentFormMode = "create" | "edit";

type AppointmentFormData = z.infer<typeof appointmentFormSchema>;
type AppointmentFormStatus = z.infer<typeof appointmentStatusSchema>;
type AppointmentFormGroomingStatus = z.infer<typeof groomingStatusSchema>;

const buildAppointmentSchema = (_mode: AppointmentFormMode) => appointmentFormSchema;

interface AppointmentFormProps {
  clinicId: string;
  mode?: AppointmentFormMode;
  appointmentId?: string;
  initialAppointment?: AppointmentWithDetails;
  onDeleteSuccess?: () => void;
  onSuccess?: () => void;
  prefilledDate?: Date;
  prefilledTime?: string;
  prefilledEndTime?: string;
  prefilledOwnerId?: string;
  prefilledPetId?: string;
  prefilledPetIds?: string[];
  prefilledVetId?: string;
  prefilledType?: string;
  prefilledBehavior?: AppointmentTypeBehavior;
  onClose?: () => void;
  mobileLayout?: boolean;
}

interface SelectedServiceItem {
  itemId?: string; // items table (products, services)
  _key: string; // stable key for list/update/remove (itemId or legacy id)
  title: string;
  unitPrice: number;
  quantity: number;
  type?: "PRODUTO" | "SERVICO" | "PACOTE";
  isVaccine?: boolean;
  productSubtypeName?: string | null;
}

const EMPTY_STRING_ARRAY: string[] = [];
const EMPTY_NUMBER_ARRAY: number[] = [];

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const toFormAppointmentStatus = (value: AppointmentStatus | null | undefined): AppointmentFormStatus => {
  return typeof value === "string" && value.trim() !== "" ? value : "agendado";
};

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sáb" },
];

const APPOINTMENT_DRIVER_TYPE_OPTIONS: AppointmentDriverType[] = [
  "pickup_and_dropoff",
  "pickup",
  "dropoff",
];

/**
 * Formata documento (CPF/CNPJ) para exibição
 */
const formatDocument = (document: string | null): string => {
  if (!document) return "";

  const digits = document.replace(/\D/g, "");

  // Se for CPF (11 dígitos)
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }

  // Se for CNPJ (14 dígitos)
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }

  return document;
};

export function AppointmentForm({
  ...props
}: AppointmentFormProps) {
  return (
    <DialogContent
      className="max-h-[90vh] max-w-5xl border border-border/60 bg-white p-0"
      noScrollViewport
      closeClassName="right-8 top-6"
    >
      <AppointmentFormBody {...props} />
    </DialogContent>
  );
}

const addOneWeek = (date: Date) => {
  const next = new Date(date);
  next.setDate(next.getDate() + 7);
  return next;
};

const toLocalDateIso = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const sortNumberArray = (values: number[]) => [...values].sort((a, b) => a - b);

const parseLocalDateIso = (value: string): Date | null => {
  const [year, month, day] = value.split("-").map((part) => Number(part));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const getAppointmentPetIds = (appointment?: AppointmentWithDetails | null): string[] => {
  if (!appointment) return [];
  const fromRelation = (appointment.pets ?? [])
    .map((pet) => (pet as { id?: string } | null | undefined)?.id)
    .filter((id): id is string => Boolean(id));
  if (fromRelation.length > 0) return Array.from(new Set(fromRelation));
  if (appointment.pet_id) return [appointment.pet_id];
  return [];
};

export function AppointmentFormBody({
  clinicId,
  mode: modeProp,
  appointmentId,
  initialAppointment,
  onDeleteSuccess,
  onSuccess,
  prefilledDate,
  prefilledTime,
  prefilledEndTime,
  prefilledOwnerId,
  prefilledPetId,
  prefilledPetIds,
  prefilledVetId,
  prefilledType,
  prefilledBehavior,
  onClose,
  mobileLayout = false,
}: AppointmentFormProps) {
  const { t } = useTranslation(["calendar", "common"]);
  const { has: hasModule } = useModuleAccess();
  const hasFinancial = hasModule("financial");
  const mode: AppointmentFormMode = modeProp ?? "create";
  const createAppointment = useCreateAppointment();
  const createRecurringAppointments = useCreateRecurringAppointments();
  const updateAppointment = useUpdateAppointment();
  const deleteAppointment = useDeleteAppointment();
  const replaceAppointmentItems = useReplaceAppointmentItems();
  const queryClient = useQueryClient();
  const { data: clinicDurationDefaults } = useClinicDefaultAppointmentDurations();
  const durationMinutesByType = useMemo(
    () => clinicDurationDefaults?.minutesByType ?? {},
    [clinicDurationDefaults],
  );
  const { data: clinicTypes = [] } = useClinicAppointmentTypes();
  const prefilledTypeFromBehavior = useMemo(
    () =>
      prefilledBehavior
        ? clinicTypes.find((t) => t.behavior === prefilledBehavior && t.active)?.id
        : undefined,
    [clinicTypes, prefilledBehavior],
  );
  const resolvedPrefilledType = prefilledType ?? prefilledTypeFromBehavior;
  // Seleção de clínica (apenas na criação, quando o usuário acessa mais de uma).
  // Os veterinários acompanham a clínica escolhida, pois são específicos por clínica.
  const { visibleClinicIds, clinicsById, hasMultipleVisible } = useVisibleClinics();
  const canSelectClinic = mode === "create" && hasMultipleVisible;
  const [selectedClinicId, setSelectedClinicId] = useState<string>(clinicId);
  const { data: veterinarians = [] } = useVeterinarians(selectedClinicId);
  const [vetDisplayValue, setVetDisplayValue] = useState("");
  const [ownerDisplayValue, setOwnerDisplayValue] = useState("");
  const [serviceDisplayValue, setServiceDisplayValue] = useState("");
  const [selectedPetDeathSnapshot, setSelectedPetDeathSnapshot] = useState<Record<string, { statusName: string | null; deathDate: string | null }>>({});
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [serviceQuantity, setServiceQuantity] = useState<number>(1);
  const [serviceItems, setServiceItems] = useState<SelectedServiceItem[]>([]);
  const [dateInput, setDateInput] = useState("");
  const [isDateCalendarOpen, setIsDateCalendarOpen] = useState(false);
  const { inputRef: dateInputRef, captureCaret: captureDateCaret } = useMaskedDigitsCaret(dateInput);
  const [recurrenceEndDateInput, setRecurrenceEndDateInput] = useState("");
  const [isRecurrenceEndCalendarOpen, setIsRecurrenceEndCalendarOpen] = useState(false);
  const {
    inputRef: recurrenceEndDateInputRef,
    captureCaret: captureRecurrenceEndDateCaret,
  } = useMaskedDigitsCaret(recurrenceEndDateInput);
  const { data: itemsData, refetch: refetchItems } = useItems({
    isActive: true,
    excludeTypes: ["PACOTE"],
  });
  const items = useMemo(() => {
    const raw = Array.isArray(itemsData) ? itemsData : (itemsData as { data?: Item[] } | undefined)?.data ?? [];
    return (raw as Item[]).filter((i) => i.type === "PRODUTO" || i.type === "SERVICO");
  }, [itemsData]);
  const [isWhoEditing, setIsWhoEditing] = useState<boolean>(mode === "create" ? !prefilledPetId : false);
  const [selectedOwnerSnapshot, setSelectedOwnerSnapshot] = useState<OwnerWithContacts | null>(null);
  const [activePetId, setActivePetId] = useState<string | null>(prefilledPetId ?? null);
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const [registerMode, setRegisterMode] = useState<"owner_and_pet" | "pet_only">("owner_and_pet");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteScopeDialogOpen, setIsDeleteScopeDialogOpen] = useState(false);
  const [isDeletingRecurringFuture, setIsDeletingRecurringFuture] = useState(false);
  const [didInitItemsFromOrder, setDidInitItemsFromOrder] = useState(false);
  const [isRecurrenceScopeDialogOpen, setIsRecurrenceScopeDialogOpen] = useState(false);
  const [pendingRecurringSubmitData, setPendingRecurringSubmitData] = useState<AppointmentFormData | null>(null);
  const [isRecurrenceOverflowDialogOpen, setIsRecurrenceOverflowDialogOpen] = useState(false);
  const [pendingRecurrenceOverflowCount, setPendingRecurrenceOverflowCount] = useState(0);
  const [isApplyingRecurrenceAction, setIsApplyingRecurrenceAction] = useState(false);
  const [isDeceasedSubmitDialogOpen, setIsDeceasedSubmitDialogOpen] = useState(false);
  const [pendingDeceasedSubmitData, setPendingDeceasedSubmitData] = useState<AppointmentFormData | null>(null);
  const [packageDrafts, setPackageDrafts] = useState<AppointmentPackageDraft[]>([]);
  const [didInitPackageDraft, setDidInitPackageDraft] = useState(false);

  const derivedAppointmentId = mode === "edit" ? (appointmentId ?? initialAppointment?.id ?? "") : "";
  const { data: appointmentStatuses = [] } = useAppointmentStatuses("appointment");
  const { data: groomingStatuses = [] } = useAppointmentStatuses("grooming");
  const { toast } = useToast();
  const {
    data: existingAppointmentPackages = [],
    isLoading: isLoadingAppointmentPackage,
  } = useAppointmentPatientPackages(mode === "edit" ? derivedAppointmentId : undefined);
  const replaceAppointmentPatientPackages = useReplaceAppointmentPatientPackages();
  const commitAppointmentPatientPackages = useCommitAppointmentPatientPackages();

  const debouncedOwnerSearch = useDebouncedValue(ownerDisplayValue.trim(), 300);
  const { data: patientSearchOptions = [], isLoading: isLoadingOwnerSearch } =
    usePatientSearchForQuickStart(debouncedOwnerSearch);
  const isDebouncingOwnerSearch =
    ownerDisplayValue.trim().length > 0 && ownerDisplayValue.trim() !== debouncedOwnerSearch;
  const showOwnerSearchLoading = isLoadingOwnerSearch || isDebouncingOwnerSearch;

  const isEditingGroomingAppointment = mode === "edit" && initialAppointment?.clinic_appointment_type?.behavior === BEHAVIOR_BANHO_TOSA;
  const { data: appointmentBelongings = [] } = useAppointmentBelongings(
    derivedAppointmentId || undefined,
    isEditingGroomingAppointment,
  );
  const belongingsSummary = useMemo(
    () => summarizeAppointmentBelongingsChecklist(appointmentBelongings),
    [appointmentBelongings],
  );
  const startsAt = useMemo(() => {
    if (mode !== "edit") return null;
    return initialAppointment?.starts_at ? new Date(initialAppointment.starts_at) : null;
  }, [initialAppointment?.starts_at, mode]);

  const endsAt = useMemo(() => {
    if (mode !== "edit") return null;
    return initialAppointment?.ends_at ? new Date(initialAppointment.ends_at) : null;
  }, [initialAppointment?.ends_at, mode]);

  const derivedPrefilledDate = useMemo(() => {
    if (mode === "edit") return startsAt ?? prefilledDate;
    return prefilledDate;
  }, [mode, prefilledDate, startsAt]);

  const derivedPrefilledTime = useMemo(() => {
    if (mode === "edit") {
      return startsAt
        ? `${startsAt.getHours().toString().padStart(2, "0")}:${startsAt.getMinutes().toString().padStart(2, "0")}`
        : prefilledTime;
    }
    return prefilledTime;
  }, [mode, prefilledTime, startsAt]);

  const derivedPrefilledEndTime = useMemo(() => {
    if (mode === "edit") {
      // In edit mode, end time is derived from `endsAt` to avoid mismatch.
      return undefined;
    }
    return prefilledEndTime;
  }, [mode, prefilledEndTime]);

  const derivedOwnerId = mode === "edit" ? (prefilledOwnerId ?? initialAppointment?.owner_id) : prefilledOwnerId;
  const derivedVetId = mode === "edit" ? initialAppointment?.vet_user_id ?? "" : prefilledVetId ?? "";
  const derivedPetIds = useMemo(() => {
    if (mode === "edit") {
      return (
        prefilledPetIds ??
        (prefilledPetId ? [prefilledPetId] : undefined) ??
        getAppointmentPetIds(initialAppointment)
      );
    }
    return prefilledPetIds ?? (prefilledPetId ? [prefilledPetId] : []);
  }, [initialAppointment, mode, prefilledPetId, prefilledPetIds]);

  const derivedEndTime = useMemo(() => {
    if (mode === "edit") {
      if (endsAt) {
        return `${endsAt.getHours().toString().padStart(2, "0")}:${endsAt.getMinutes().toString().padStart(2, "0")}`;
      }
      return derivedPrefilledTime
        ? addMinutesToTimeString(
            derivedPrefilledTime,
            resolveDefaultDurationMinutes(initialAppointment?.clinic_appointment_type?.behavior, durationMinutesByType),
          )
        : "";
    }
    if (derivedPrefilledEndTime) return derivedPrefilledEndTime;
    return derivedPrefilledTime
      ? addMinutesToTimeString(
          derivedPrefilledTime,
          resolveDefaultDurationMinutes(resolvedPrefilledType ? clinicTypes.find(t => t.id === resolvedPrefilledType)?.behavior : undefined, durationMinutesByType),
        )
      : "";
  }, [
    clinicTypes,
    derivedPrefilledEndTime,
    derivedPrefilledTime,
    durationMinutesByType,
    endsAt,
    initialAppointment?.clinic_appointment_type?.behavior,
    mode,
    resolvedPrefilledType,
  ]);

  const initialAppointmentStatus: AppointmentFormStatus =
    mode === "edit"
      ? (() => {
        const rawStatus = initialAppointment?.status;
        const rawGroomingStatus = initialAppointment?.grooming_status;
        if (initialAppointment?.clinic_appointment_type?.behavior === BEHAVIOR_BANHO_TOSA) {
          return toFormAppointmentStatus(rawStatus);
        }

        if (typeof rawStatus === "string" && rawStatus) {
          return toFormAppointmentStatus(rawStatus);
        }

        return getFirstActiveStatusName(appointmentStatuses);
      })()
      : getFirstActiveStatusName(appointmentStatuses);

  const initialGroomingStatus: AppointmentFormGroomingStatus =
    mode === "edit" && initialAppointment?.clinic_appointment_type?.behavior === BEHAVIOR_BANHO_TOSA
      ? (() => {
        const rawStatus = initialAppointment?.status;
        const rawGroomingStatus = initialAppointment?.grooming_status;
        const fromColumn =
          (typeof rawGroomingStatus === "string" && rawGroomingStatus) ||
          (typeof rawStatus === "string" && getStatusConfigByName(rawStatus, groomingStatuses)?.name);
        if (fromColumn) {
          return fromColumn === "pago" || fromColumn === "entregue" ? "finalizado" : fromColumn;
        }
        return getFirstActiveStatusName(groomingStatuses);
      })()
      : getFirstActiveStatusName(groomingStatuses);

  const { data: appointmentItemsData } = useAppointmentItems(mode === "edit" ? derivedAppointmentId : undefined);
  const appointmentItems = useMemo(
    () =>
      (mode === "edit" ? (appointmentItemsData ?? []) : []) as Array<{
        id: string;
        appointment_id: string;
        item_id: string;
        qty: number;
        title_snapshot: string;
        item?: { type?: string; track_by_batch?: boolean; track_by_expiry?: boolean } | null;
      }>,
    [appointmentItemsData, mode],
  );

  const form = useForm<AppointmentFormData>({
    resolver: zodResolver(buildAppointmentSchema(mode)),
    defaultValues: {
      clinic_id: clinicId,
      type: resolvedPrefilledType ?? "",
      notes: "",
      ...(mode === "edit"
        ? {
          owner_id: derivedOwnerId,
          pet_ids: derivedPetIds ?? [],
          vet_user_id: initialAppointment?.vet_user_id ?? "",
          type: initialAppointment?.type ?? resolvedPrefilledType ?? "",
          notes: initialAppointment?.notes ?? "",
          is_taxi_dog: initialAppointment?.is_taxi_dog ?? false,
          taxi_dog_type: getEffectiveAppointmentDriverType({
            driverType: initialAppointment?.taxi_dog_type,
            isTaxiDog: initialAppointment?.is_taxi_dog,
          }),
          date: derivedPrefilledDate,
          start_time: derivedPrefilledTime || "",
          end_time: derivedEndTime,
          status: initialAppointmentStatus,
          grooming_status: initialGroomingStatus,
          is_recurring: Boolean(initialAppointment?.recurrence_id),
          recurrence_frequency: initialAppointment?.recurrence?.frequency ?? "weekly",
          recurrence_interval: initialAppointment?.recurrence?.interval ?? 1,
          recurrence_weekdays: initialAppointment?.recurrence?.weekdays ?? [],
          recurrence_end_type: initialAppointment?.recurrence?.ends_on
            ? "date"
            : initialAppointment?.recurrence?.max_occurrences
              ? "count"
              : "none",
          recurrence_ends_on: initialAppointment?.recurrence?.ends_on
            ? parseLocalDateIso(initialAppointment.recurrence.ends_on) ?? undefined
            : undefined,
          recurrence_max_occurrences: initialAppointment?.recurrence?.max_occurrences ?? undefined,
        }
        : {
          date: derivedPrefilledDate,
          start_time: derivedPrefilledTime || "",
          end_time: derivedEndTime,
          owner_id: derivedOwnerId,
          pet_ids: derivedPetIds ?? [],
          vet_user_id: derivedVetId,
          type: resolvedPrefilledType ?? "",
          is_taxi_dog: false,
          taxi_dog_type: null,
          status: initialAppointmentStatus,
          grooming_status: initialGroomingStatus,
          is_recurring: false,
          recurrence_frequency: "weekly",
          recurrence_interval: 1,
          recurrence_weekdays: [],
          recurrence_end_type: "date",
          recurrence_ends_on: derivedPrefilledDate ? addOneWeek(derivedPrefilledDate) : undefined,
          recurrence_max_occurrences: undefined,
        }),
    },
  });

  const resetFormState = useCallback(() => {
    const defaultStartTime = derivedPrefilledTime || "";
    const defaultEndTime = derivedEndTime || "";

    if (mode === "edit") {
      form.reset(
        {
          clinic_id: clinicId,
          owner_id: derivedOwnerId,
          pet_ids: derivedPetIds ?? [],
          vet_user_id: initialAppointment?.vet_user_id ?? "",
          type: initialAppointment?.type ?? "",
          notes: initialAppointment?.notes ?? "",
          is_taxi_dog: initialAppointment?.is_taxi_dog ?? false,
          taxi_dog_type: getEffectiveAppointmentDriverType({
            driverType: initialAppointment?.taxi_dog_type,
            isTaxiDog: initialAppointment?.is_taxi_dog,
          }),
          date: derivedPrefilledDate,
          start_time: defaultStartTime,
          end_time: defaultEndTime,
          status: initialAppointmentStatus,
          grooming_status: initialGroomingStatus,
          is_recurring: Boolean(initialAppointment?.recurrence_id),
          recurrence_frequency: initialAppointment?.recurrence?.frequency ?? "weekly",
          recurrence_interval: initialAppointment?.recurrence?.interval ?? 1,
          recurrence_weekdays: initialAppointment?.recurrence?.weekdays ?? [],
          recurrence_end_type: initialAppointment?.recurrence?.ends_on
            ? "date"
            : initialAppointment?.recurrence?.max_occurrences
              ? "count"
              : "none",
          recurrence_ends_on: initialAppointment?.recurrence?.ends_on
            ? parseLocalDateIso(initialAppointment.recurrence.ends_on) ?? undefined
            : undefined,
          recurrence_max_occurrences: initialAppointment?.recurrence?.max_occurrences ?? undefined,
        } as Partial<AppointmentFormData>,
      );
      form.clearErrors();
      setSelectedItemId("");
      setServiceQuantity(1);
      setIsWhoEditing(false);
      setSelectedOwnerSnapshot(null);
      setActivePetId((derivedPetIds?.[0] ?? null) as string | null);
      setVetDisplayValue("");
      setOwnerDisplayValue("");
      setServiceDisplayValue("");
      setSelectedPetDeathSnapshot({});
      setIsDateCalendarOpen(false);
      setDidInitItemsFromOrder(false);
      setDidInitPackageDraft(false);

      if (derivedPrefilledDate) {
        const iso = toLocalDateIso(derivedPrefilledDate);
        setDateInput(formatISOToDisplay(iso));
      } else {
        setDateInput("");
      }
      if (initialAppointment?.recurrence?.ends_on) {
        setRecurrenceEndDateInput(formatISOToDisplay(initialAppointment.recurrence.ends_on));
      } else {
        setRecurrenceEndDateInput("");
      }
      return;
    }

    form.reset(
      {
        clinic_id: clinicId,
        type: "",
        notes: "",
        is_taxi_dog: false,
        taxi_dog_type: null,
        date: derivedPrefilledDate,
        start_time: defaultStartTime,
        end_time: defaultEndTime,
        owner_id: undefined,
        pet_ids: [],
        vet_user_id: derivedVetId,
        status: initialAppointmentStatus,
        grooming_status: initialGroomingStatus,
        is_recurring: false,
        recurrence_frequency: "weekly",
        recurrence_interval: 1,
        recurrence_weekdays: [],
        recurrence_end_type: "date",
        recurrence_ends_on: derivedPrefilledDate ? addOneWeek(derivedPrefilledDate) : undefined,
        recurrence_max_occurrences: undefined,
      } as Partial<AppointmentFormData>,
    );
    form.clearErrors();
    setServiceItems([]);
    setSelectedItemId("");
    setServiceQuantity(1);
    setIsWhoEditing(!prefilledPetId);
    setSelectedOwnerSnapshot(null);
    setActivePetId(prefilledPetId ?? null);
    setVetDisplayValue("");
    setOwnerDisplayValue("");
    setServiceDisplayValue("");
    setSelectedPetDeathSnapshot({});
    setIsDateCalendarOpen(false);
    setPackageDrafts([]);
    setDidInitPackageDraft(true);

    if (derivedPrefilledDate) {
      const iso = toLocalDateIso(derivedPrefilledDate);
      setDateInput(formatISOToDisplay(iso));
    } else {
      setDateInput("");
    }
    setRecurrenceEndDateInput("");
  }, [
    clinicId,
    derivedEndTime,
    derivedOwnerId,
    derivedPetIds,
    derivedPrefilledDate,
    derivedPrefilledTime,
    derivedVetId,
    form,
    initialAppointment,
    initialAppointmentStatus,
    initialGroomingStatus,
    mode,
    prefilledPetId,
  ]);

  const handleCancel = useCallback(() => {
    resetFormState();
    onClose?.();
  }, [onClose, resetFormState]);

  const selectedOwnerId = useWatch({ control: form.control, name: "owner_id" });
  const selectedVetId = useWatch({ control: form.control, name: "vet_user_id" });
  const selectedPetIds = useWatch({ control: form.control, name: "pet_ids" }) ?? EMPTY_STRING_ARRAY;
  const selectedType = useWatch({ control: form.control, name: "type" });
  const selectedClinicType = clinicTypes.find((t) => t.id === selectedType);
  const selectedBehavior = selectedClinicType?.behavior ?? null;
  const isGrooming = selectedBehavior === BEHAVIOR_BANHO_TOSA;
  const { data: ownerPetsData = [] } = usePetsByOwner(selectedOwnerId);
  const { data: selectedOwnerData } = useOwner(selectedOwnerId ?? "");
  const isRecurring = useWatch({ control: form.control, name: "is_recurring" }) ?? false;
  const recurrenceFrequency = useWatch({ control: form.control, name: "recurrence_frequency" }) ?? "weekly";
  const recurrenceInterval = useWatch({ control: form.control, name: "recurrence_interval" }) ?? 1;
  const recurrenceWeekdays = useWatch({ control: form.control, name: "recurrence_weekdays" }) ?? EMPTY_NUMBER_ARRAY;
  const recurrenceEndType = useWatch({ control: form.control, name: "recurrence_end_type" }) ?? "date";
  const recurrenceEndsOn = useWatch({ control: form.control, name: "recurrence_ends_on" });
  const recurrenceMaxOccurrences = useWatch({ control: form.control, name: "recurrence_max_occurrences" });
  const recurrenceDate = useWatch({ control: form.control, name: "date" });
  const recurrenceStartTime = useWatch({ control: form.control, name: "start_time" });
  const recurrenceEndTime = useWatch({ control: form.control, name: "end_time" });
  const isTaxiDogEnabled = useWatch({ control: form.control, name: "is_taxi_dog" }) ?? false;
  const selectedTaxiDogType = useWatch({ control: form.control, name: "taxi_dog_type" });
  const statusValue = useWatch({ control: form.control, name: "status" }) ?? "agendado";
  const groomingStatusValue = useWatch({ control: form.control, name: "grooming_status" }) ?? "agendado";
  const statusSidePanelMode = isGrooming ? "grooming" : "appointment";
  const statusSidePanelValue = statusSidePanelMode === "grooming" ? groomingStatusValue : statusValue;
  const sidePanelStatuses = statusSidePanelMode === "grooming" ? groomingStatuses : appointmentStatuses;
  const isSaving = createAppointment.isPending || createRecurringAppointments.isPending || updateAppointment.isPending;
  const isPackageSectionLocked =
    isSaving ||
    (mode === "edit" && (
      initialAppointment?.status === "finalizado" ||
      existingAppointmentPackages.some((pkg) => pkg.consumption_status === "consumed")
    ));
  const isDeleting = deleteAppointment.isPending || isDeletingRecurringFuture;

  useEffect(() => {
    if (isTaxiDogEnabled) {
      if (!selectedTaxiDogType) {
        form.setValue("taxi_dog_type", DEFAULT_APPOINTMENT_DRIVER_TYPE, { shouldDirty: false });
      }
      return;
    }

    if (selectedTaxiDogType !== null && selectedTaxiDogType !== undefined) {
      form.setValue("taxi_dog_type", null, { shouldDirty: false });
    }
  }, [form, isTaxiDogEnabled, selectedTaxiDogType]);

  const buildNextPetIdsForOwnerSelection = useCallback((
    nextOwnerId: string,
    primaryPetId: string,
    currentPetIds: string[] | undefined,
  ) => {
    if (!nextOwnerId || !primaryPetId) return [] as string[];

    if (selectedOwnerId !== nextOwnerId) {
      return [primaryPetId];
    }

    const preservedAdditionalPets = (currentPetIds ?? []).filter((petId) => petId && petId !== primaryPetId);
    return [primaryPetId, ...preservedAdditionalPets];
  }, [selectedOwnerId]);

  const firstAppointmentStatusName = useMemo(
    () => getFirstActiveStatusName(appointmentStatuses),
    [appointmentStatuses],
  );
  const firstGroomingStatusName = useMemo(
    () => getFirstActiveStatusName(groomingStatuses),
    [groomingStatuses],
  );

  useEffect(() => {
    if (isGrooming) {
      if (groomingStatuses.length === 0) return;
      const currentGroomingStatus = form.getValues("grooming_status");
      if (
        typeof currentGroomingStatus !== "string" ||
        !groomingStatuses.some((status) => status.is_active && status.name === currentGroomingStatus)
      ) {
        form.setValue("grooming_status", firstGroomingStatusName, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      return;
    }

    if (appointmentStatuses.length === 0) return;
    const currentStatus = form.getValues("status");
    if (
      typeof currentStatus !== "string" ||
      !appointmentStatuses.some((status) => status.is_active && status.name === currentStatus)
    ) {
      form.setValue("status", firstAppointmentStatusName, { shouldDirty: true, shouldValidate: true });
    }

    const currentGroomingStatus = form.getValues("grooming_status");
    if (currentGroomingStatus !== undefined) {
      form.setValue("grooming_status", undefined, { shouldDirty: true, shouldValidate: false });
    }
  }, [
    appointmentStatuses,
    firstAppointmentStatusName,
    firstGroomingStatusName,
    form,
    groomingStatuses,
    isGrooming,
  ]);

  // Pré-preencher data e horário quando o usuário abre o modal (create)
  useEffect(() => {
    if (mode !== "create") return;
    if (derivedPrefilledDate) {
      form.setValue("date", derivedPrefilledDate);
      const iso = toLocalDateIso(derivedPrefilledDate);
      setDateInput(formatISOToDisplay(iso));
    }
    if (derivedPrefilledTime) {
      form.setValue("start_time", derivedPrefilledTime);
      form.setValue(
        "end_time",
        derivedEndTime ||
          addMinutesToTimeString(
            derivedPrefilledTime,
            resolveDefaultDurationMinutes(resolvedPrefilledType ? clinicTypes.find(t => t.id === resolvedPrefilledType)?.behavior : undefined, durationMinutesByType),
          ),
      );
    }
  }, [clinicTypes, derivedEndTime, derivedPrefilledDate, derivedPrefilledTime, durationMinutesByType, form, mode, resolvedPrefilledType]);

  useEffect(() => {
    if (mode !== "create") return;
    if (!derivedVetId) return;
    form.setValue("vet_user_id", derivedVetId);
  }, [derivedVetId, form, mode]);

  // Setar `dateInput` inicial no modo edição (sem sobrescrever os valores do form durante a digitação)
  useEffect(() => {
    if (mode !== "edit") return;
    if (!startsAt) return;
    const iso = toLocalDateIso(startsAt);
    setDateInput(formatISOToDisplay(iso));
  }, [mode, startsAt]);

  useEffect(() => {
    if (!recurrenceEndsOn) {
      setRecurrenceEndDateInput("");
      return;
    }
    setRecurrenceEndDateInput(formatISOToDisplay(toLocalDateIso(recurrenceEndsOn)));
  }, [recurrenceEndsOn]);

  useEffect(() => {
    if (!isRecurring) return;
    if (recurrenceEndType !== "date") return;
    if (recurrenceEndsOn) return;
    if (!recurrenceDate) return;
    form.setValue("recurrence_ends_on", addOneWeek(recurrenceDate), {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [form, isRecurring, recurrenceDate, recurrenceEndType, recurrenceEndsOn]);

  // Pré-preencher tutor e pets (hub/create/edit)
  useEffect(() => {
    if (!derivedOwnerId) return;
    form.setValue("owner_id", derivedOwnerId);

    if (derivedPetIds && derivedPetIds.length > 0) {
      const owned = derivedPetIds.filter((petId) =>
        ownerPetsData.some((pet) => pet.id === petId),
      );
      if (owned.length > 0) {
        form.setValue("pet_ids", owned);
        setActivePetId((prev) => prev ?? owned[0] ?? null);
      }
    }
  }, [derivedOwnerId, derivedPetIds, form, ownerPetsData]);

  const isAppointmentPaid =
    mode === "edit" &&
    initialAppointment != null &&
    isFinancialSettled(initialAppointment.appointment_financial, {
      status: initialAppointment.status,
      grooming_status: initialAppointment.grooming_status,
    });

  // Inicializar itens (produtos/serviços) a partir de appointment_items ao entrar em modo edição
  useEffect(() => {
    if (mode !== "edit") return;
    if (!derivedAppointmentId) return;
    if (didInitItemsFromOrder) return;
    if (!Array.isArray(appointmentItems)) return;

    const mapped = appointmentItems
      .filter((ai) => (ai.item?.type ?? items.find((item) => item.id === ai.item_id)?.type) !== "PACOTE")
      .map((ai, index) => {
        const item = items.find((i) => i.id === ai.item_id);
        const resolvedItem = (ai.item ?? item) as
          | {
              name?: string | null;
              sale_price?: number | null;
              type?: string;
              track_by_batch?: boolean | null;
              track_by_expiry?: boolean | null;
              product_subtype?: { name?: string | null } | null;
            }
          | undefined;
        const type = (ai.item?.type ?? item?.type) as SelectedServiceItem["type"] | undefined;
        const productSubtypeName = resolvedItem?.product_subtype?.name ?? null;
        const isVaccine = Boolean(
          resolvedItem &&
            resolvedItem.type === "PRODUTO" &&
            ((resolvedItem.product_subtype?.name?.toLowerCase() === "vacina") ||
              (resolvedItem.track_by_batch && resolvedItem.track_by_expiry)),
        );
        const unitPrice = resolvedItem?.sale_price ?? 0;
        return {
          itemId: ai.item_id,
          _key: ai.item_id,
          title: ai.title_snapshot?.trim() || item?.name || "",
          unitPrice,
          quantity: ai.qty ?? 1,
          type,
          isVaccine,
          productSubtypeName,
        } satisfies SelectedServiceItem;
      });

    setServiceItems(mapped);
    setDidInitItemsFromOrder(true);
  }, [derivedAppointmentId, didInitItemsFromOrder, appointmentItems, items, mode]);

  type UnifiedItem = {
    id: string;
    name: string;
    price: number;
    type?: "PRODUTO" | "SERVICO" | "PACOTE";
    isVaccine?: boolean;
    description?: string | null;
    productSubtypeName?: string | null;
  };

  const unifiedItems = useMemo<UnifiedItem[]>(() => {
    return items
      .filter((item) => isItemAllowedForBehavior(item.allowed_appointment_types, selectedBehavior))
      .filter((item) => item.type !== "PACOTE")
      .map((item) => {
        const isVaccine =
          item.type === "PRODUTO" &&
          (item.product_subtype?.name?.toLowerCase() === "vacina" ||
            (item.track_by_batch && item.track_by_expiry));
        return {
          id: item.id,
          name: item.name,
          price: item.sale_price ?? 0,
          type: item.type,
          isVaccine,
          productSubtypeName: item.product_subtype?.name ?? null,
          description: item.description,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, selectedType]);

  const ownerPets = useMemo<PetWithOwner[]>(
    () => ((ownerPetsData ?? []) as PetWithOwner[]),
    [ownerPetsData],
  );
  const primarySelectedPetId = selectedPetIds[0] ?? null;
  const selectedPatientAutocompleteValue = useMemo(() => {
    if (!selectedOwnerId) return "";
    return primarySelectedPetId ? `${selectedOwnerId}:${primarySelectedPetId}` : "";
  }, [primarySelectedPetId, selectedOwnerId]);
  const additionalOwnerPets = useMemo(
    () => ownerPets.filter((pet) => pet.id !== primarySelectedPetId),
    [ownerPets, primarySelectedPetId],
  );
  const hasMorePatientsToAdd = useMemo(
    () => additionalOwnerPets.some((pet) => !selectedPetIds.includes(pet.id)),
    [additionalOwnerPets, selectedPetIds],
  );
  const selectedPets = useMemo(
    () => ownerPets.filter((pet) => selectedPetIds.includes(pet.id)),
    [ownerPets, selectedPetIds],
  );
  const deceasedSelectedPets = useMemo(
    () => selectedPets.filter((pet) => isPetDeceased(pet)),
    [selectedPets],
  );
  const hasDeceasedSelectedPet = deceasedSelectedPets.length > 0;

  useEffect(() => {
    if (mode !== "edit") return;
    if (didInitPackageDraft) return;
    if (isLoadingAppointmentPackage) return;

    if (existingAppointmentPackages.length > 0) {
      setPackageDrafts(existingAppointmentPackages.map((pkg) => ({
        id: pkg.id,
        mode: pkg.mode,
        petId: pkg.pet_id,
        catalogPackageId: pkg.catalog_package_id,
        patientPackageId: pkg.patient_package_id,
        sessionsToConsume: pkg.sessions_to_consume,
        title: pkg.patient_package?.catalog_package?.item?.name ?? pkg.catalog_package?.item?.name ?? "Pacote",
        price: pkg.patient_package?.catalog_package?.item?.sale_price ?? pkg.catalog_package?.item?.sale_price ?? 0,
        financialStatus: pkg.patient_package?.status_financeiro ?? "pendente",
        controlsSessions: pkg.patient_package?.catalog_package?.controls_sessions ?? pkg.catalog_package?.controls_sessions ?? true,
        remainingSessions: pkg.patient_package?.sessions_remaining ?? pkg.catalog_package?.default_sessions ?? 0,
        totalSessions: pkg.patient_package?.sessions_total ?? pkg.catalog_package?.default_sessions ?? 0,
        billingIntent: pkg.billing_intent,
        suggestedChargeAmount: pkg.suggested_charge_amount,
        allowedAppointmentTypes:
          pkg.patient_package?.catalog_package?.item?.allowed_appointment_types ??
          pkg.catalog_package?.item?.allowed_appointment_types ??
          [],
      })));
      setActivePetId((prev) => prev ?? existingAppointmentPackages[0]?.pet_id ?? null);
    } else {
      setPackageDrafts([]);
    }

    setDidInitPackageDraft(true);
  }, [didInitPackageDraft, existingAppointmentPackages, isLoadingAppointmentPackage, mode]);

  useEffect(() => {
    if (mode !== "create") return;
    setPackageDrafts([]);
    setDidInitPackageDraft(true);
  }, [mode]);

  useEffect(() => {
    if (!isRecurring) return;
    if (packageDrafts.length > 0) {
      setPackageDrafts([]);
    }
  }, [isRecurring, packageDrafts]);

  useEffect(() => {
    if (packageDrafts.length === 0) return;
    setPackageDrafts((current) => current.filter((draft) => selectedPetIds.includes(draft.petId)));
  }, [packageDrafts.length, selectedPetIds]);

  const persistAppointmentPackageLinks = useCallback(async (
    targetAppointmentId: string,
    shouldCommit: boolean,
  ) => {
    if (!targetAppointmentId) return;

    for (const draft of packageDrafts) {
      if (draft.mode === "new" && !draft.catalogPackageId) {
        throw new Error("Selecione um pacote do catálogo para cada novo pacote adicionado.");
      }

      if (draft.mode === "existing" && !draft.patientPackageId) {
        throw new Error("Selecione um pacote existente para cada utilização adicionada.");
      }

      if (draft.mode === "new" && draft.billingIntent === "partial_now" && (draft.suggestedChargeAmount ?? 0) <= 0) {
        throw new Error("Informe um valor valido para cobranca parcial.");
      }
    }

    await replaceAppointmentPatientPackages.mutateAsync({
      appointmentId: targetAppointmentId,
      links: packageDrafts.map((draft) => ({
        id: draft.id,
        appointmentId: targetAppointmentId,
        clinicId,
        petId: draft.petId,
        mode: draft.mode,
        patientPackageId: draft.patientPackageId,
        catalogPackageId: draft.catalogPackageId,
        sessionsToConsume: draft.sessionsToConsume,
        consumptionStatus: "planned",
        billingIntent: draft.billingIntent,
        suggestedChargeAmount: draft.suggestedChargeAmount,
      })),
    });

    if (shouldCommit) {
      await commitAppointmentPatientPackages.mutateAsync(targetAppointmentId);
    }
  }, [
    clinicId,
    commitAppointmentPatientPackages,
    packageDrafts,
    replaceAppointmentPatientPackages,
  ]);

  const selectedVet = useMemo(
    () =>
      selectedVetId
        ? veterinarians.find((vet) => vet.id === selectedVetId) ?? null
        : null,
    [veterinarians, selectedVetId],
  );

  // Options for autocompletes
  const vetOptions = useMemo<AutocompleteOption[]>(
    () =>
      veterinarians.map((vet) => ({
        label: vet.full_name,
        value: vet.id,
      })),
    [veterinarians],
  );
  const ownerOptions = useMemo<AutocompleteOption[]>(
    () =>
      patientSearchOptions.map((option) => ({
        label: option.label,
        value: option.value,
      })),
    [patientSearchOptions],
  );
  const ownerHasExactMatch = useMemo(() => {
    const normalizedValue = ownerDisplayValue.trim().toLowerCase();
    if (!normalizedValue) return true;
    return patientSearchOptions.some((option) => option.label.toLowerCase().trim() === normalizedValue);
  }, [ownerDisplayValue, patientSearchOptions]);

  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    const item = unifiedItems.find((i) => i.id === selectedItemId);
    if (!item) return null;
    return item;
  }, [selectedItemId, unifiedItems]);
  const incompatibleSelectedItems = useMemo(
    () =>
      serviceItems.filter((serviceItem) => {
        if (!serviceItem.itemId) return false;
        const matchingItem = items.find((item) => item.id === serviceItem.itemId);
        return matchingItem ? !isItemAllowedForBehavior(matchingItem.allowed_appointment_types, selectedBehavior) : false;
      }),
    [items, selectedType, serviceItems],
  );

  // Keep an owner snapshot available when the form is prefilled or re-opened in edit mode.
  useEffect(() => {
    if (selectedOwnerSnapshot) return;
    if (!selectedOwnerId) return;
    if (!selectedOwnerData) return;
    setSelectedOwnerSnapshot((prev) => prev ?? ({
      ...selectedOwnerData,
      pets: ownerPetsData,
    }));
  }, [ownerPetsData, selectedOwnerData, selectedOwnerId, selectedOwnerSnapshot]);

  useEffect(() => {
    if (!selectedOwnerId) {
      setIsWhoEditing(true);
      return;
    }
  }, [selectedOwnerId]);

  useEffect(() => {
    if (!selectedVetId) {
      setVetDisplayValue("");
      return;
    }
    if (selectedVet?.full_name) {
      setVetDisplayValue(selectedVet.full_name);
    }
  }, [selectedVet?.full_name, selectedVetId]);

  useEffect(() => {
    if (selectedPetIds.length === 0) {
      setActivePetId(null);
      return;
    }
    if (activePetId && selectedPetIds.includes(activePetId)) return;
    setActivePetId(selectedPetIds[0] ?? null);
  }, [activePetId, selectedPetIds]);

  // Sync display values with selected values
  useEffect(() => {
    if (selectedVet) {
      setVetDisplayValue(selectedVet.full_name);
    }
    // Don't reset during typing - let onChange handlers manage the value
  }, [selectedVet]);

  useEffect(() => {
    if (selectedOwnerSnapshot) {
      const selectedPet = ownerPets.find((pet) => pet.id === primarySelectedPetId) ?? null;
      setOwnerDisplayValue(selectedPet?.name?.trim() || selectedOwnerSnapshot.name);
    }
    // Don't reset during typing - let onChange handlers manage the value
  }, [ownerPets, primarySelectedPetId, selectedOwnerSnapshot]);

  useEffect(() => {
    if (selectedItem) {
      setServiceDisplayValue(selectedItem.name);
    }
    // Don't reset during typing - let onChange handlers manage the value
  }, [selectedItem]);

  useEffect(() => {
    if (selectedItemId && !selectedItem) {
      setSelectedItemId("");
      setServiceDisplayValue("");
    }
  }, [selectedItem, selectedItemId]);

  const serviceAutocompleteOptions = useMemo<AutocompleteOption[]>(
    () =>
      unifiedItems.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [unifiedItems],
  );

  const servicesTotal = useMemo(
    () =>
      serviceItems.reduce(
        (sum, item) => sum + item.unitPrice * item.quantity,
        0,
      ),
    [serviceItems],
  );
  const packageSuggestedChargeTotal = useMemo(() => {
    return packageDrafts.reduce((total, draft) => {
      if (draft.mode !== "new") return total;
      return total + Math.max(0, draft.suggestedChargeAmount ?? 0);
    }, 0);
  }, [packageDrafts]);
  const servicesBalanceTotal = useMemo(
    () => (isAppointmentPaid ? 0 : servicesTotal),
    [isAppointmentPaid, servicesTotal],
  );
  const appointmentBalanceTotal = useMemo(
    () => servicesBalanceTotal + packageSuggestedChargeTotal,
    [packageSuggestedChargeTotal, servicesBalanceTotal],
  );

  const getItemTypeLabel = useCallback(
    (item: UnifiedItem | SelectedServiceItem) => {
      if (item.type === "PRODUTO" && item.productSubtypeName) {
        return item.productSubtypeName.charAt(0).toUpperCase() + item.productSubtypeName.slice(1);
      }
      if (item.type === "SERVICO") {
        return t("calendar:appointmentForm.service");
      }
      if (item.type === "PACOTE") {
        return t("common.package");
      }
      return t("calendar:appointmentForm.product");
    },
    [t],
  );

  const getItemTypeBadge = useCallback(
    (item: UnifiedItem | SelectedServiceItem) => {
      if (!item.type) return null;
      const label = getItemTypeLabel(item);
      const badgeClass = getItemTypeBadgeClass(item.type);
      return (
        <Badge className={cn("h-6 text-xs", badgeClass)}>
          {label}
        </Badge>
      );
    },
    [getItemTypeLabel],
  );

  const recurrenceSummary = useMemo(() => {
    if (!isRecurring) return null;
    const endsOn = recurrenceEndType === "date" ? recurrenceEndsOn : null;
    return formatRecurrenceSummary({
      frequency: recurrenceFrequency as RecurrenceFrequency,
      interval: recurrenceInterval,
      endsOn: endsOn ?? null,
    });
  }, [isRecurring, recurrenceEndType, recurrenceEndsOn, recurrenceFrequency, recurrenceInterval]);

  const recurrencePreviewDates = useMemo(() => {
    if (!isRecurring) return [];
    if (!recurrenceDate) return [];

    const startsAt = new Date(recurrenceDate);
    const normalizedStartTime = recurrenceStartTime && recurrenceStartTime.includes(":") ? recurrenceStartTime : "09:00";
    const normalizedEndTime = recurrenceEndTime && recurrenceEndTime.includes(":")
      ? recurrenceEndTime
      : addMinutesToTimeString(
          normalizedStartTime,
          resolveDefaultDurationMinutes(clinicTypes.find(t => t.id === selectedType)?.behavior, durationMinutesByType),
        );
    const [sh, sm] = normalizedStartTime.split(":");
    startsAt.setHours(parseInt(sh, 10), parseInt(sm, 10), 0, 0);
    const endsAt = new Date(recurrenceDate);
    const [eh, em] = normalizedEndTime.split(":");
    endsAt.setHours(parseInt(eh, 10), parseInt(em, 10), 0, 0);
    if (endsAt.getTime() <= startsAt.getTime()) {
      endsAt.setDate(endsAt.getDate() + 1);
    }

    const weekdays = recurrenceFrequency === "weekly"
      ? (recurrenceWeekdays.length > 0 ? recurrenceWeekdays : [startsAt.getDay()])
      : undefined;
    const occurrences = generateRecurrenceOccurrences(startsAt, endsAt, {
      frequency: recurrenceFrequency as RecurrenceFrequency,
      interval: recurrenceInterval,
      weekdays,
      monthday: startsAt.getDate(),
      endsOn: recurrenceEndType === "date" ? (recurrenceEndsOn ?? null) : null,
      maxOccurrences: recurrenceEndType === "count" ? (recurrenceMaxOccurrences ?? null) : null,
      windowDays: recurrenceEndType === "none" ? 365 : 730,
    });
    return occurrences.map((row) => row.startsAt.toLocaleDateString("pt-BR"));
  }, [
    clinicTypes,
    durationMinutesByType,
    isRecurring,
    recurrenceDate,
    recurrenceEndTime,
    recurrenceEndType,
    recurrenceEndsOn,
    recurrenceFrequency,
    recurrenceInterval,
    recurrenceMaxOccurrences,
    recurrenceStartTime,
    recurrenceWeekdays,
    selectedType,
  ]);

  const existingRecurrenceSummary = useMemo(() => {
    if (mode !== "edit" || !initialAppointment?.recurrence) return null;
    return formatRecurrenceSummary({
      frequency: initialAppointment.recurrence.frequency,
      interval: initialAppointment.recurrence.interval,
      endsOn: initialAppointment.recurrence.ends_on ?? null,
    });
  }, [initialAppointment?.recurrence, mode]);

  const handleAddService = () => {
    if (!selectedItem) return;
    const id = selectedItem.id;

    setServiceItems((prev) => {
      const existing = prev.find((item) => item.itemId === id);
      if (existing) {
        return prev.map((item) =>
          item.itemId === id ? { ...item, quantity: item.quantity + serviceQuantity } : item,
        );
      }
      return [
        ...prev,
        {
          itemId: selectedItem.id,
          _key: selectedItem.id,
          title: selectedItem.name,
          unitPrice: selectedItem.price,
          quantity: serviceQuantity,
          type: selectedItem.type,
          isVaccine: selectedItem.isVaccine,
          productSubtypeName: selectedItem.productSubtypeName,
        },
      ];
    });
    setSelectedItemId("");
    setServiceQuantity(1);
    setServiceDisplayValue("");
  };

  const updateServiceQuantity = (itemKey: string, quantity: number) => {
    const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1;
    setServiceItems((prev) =>
      prev.map((item) =>
        item._key === itemKey ? { ...item, quantity: safeQuantity } : item,
      ),
    );
  };

  const removeService = (itemKey: string) => {
    setServiceItems((prev) => prev.filter((item) => item._key !== itemKey));
  };

  const hasRecurringRuleChanges = useCallback((data: AppointmentFormData) => {
    if (mode !== "edit") return false;
    if (!initialAppointment?.recurrence_id || !initialAppointment.recurrence) return false;

    const nextFrequency = (data.recurrence_frequency ?? "weekly") as RecurrenceFrequency;
    const nextInterval = data.recurrence_interval ?? 1;
    const nextWeekdays = sortNumberArray(data.recurrence_weekdays ?? []);
    const currentWeekdays = sortNumberArray(initialAppointment.recurrence.weekdays ?? []);
    const nextEndType = data.recurrence_end_type ?? "date";
    const currentEndType = initialAppointment.recurrence.ends_on
      ? "date"
      : initialAppointment.recurrence.max_occurrences
        ? "count"
        : "none";
    const nextEndsOn = nextEndType === "date" && data.recurrence_ends_on ? toLocalDateIso(data.recurrence_ends_on) : null;
    const currentEndsOn = initialAppointment.recurrence.ends_on ?? null;
    const nextMaxOccurrences = nextEndType === "count" ? (data.recurrence_max_occurrences ?? null) : null;
    const currentMaxOccurrences = initialAppointment.recurrence.max_occurrences ?? null;

    if (nextFrequency !== initialAppointment.recurrence.frequency) return true;
    if (nextInterval !== initialAppointment.recurrence.interval) return true;
    if (JSON.stringify(nextWeekdays) !== JSON.stringify(currentWeekdays)) return true;
    if (nextEndType !== currentEndType) return true;
    if (nextEndsOn !== currentEndsOn) return true;
    if (nextMaxOccurrences !== currentMaxOccurrences) return true;
    return false;
  }, [initialAppointment?.recurrence, initialAppointment?.recurrence_id, mode]);

  const performUpdateRecurringFuture = useCallback(async (
    data: AppointmentFormData,
    startDateTime: Date,
    endDateTime: Date,
    input: Parameters<typeof updateAppointment.mutateAsync>[0]["input"],
    overflowMode: "keep" | "delete" = "keep",
  ) => {
    if (!initialAppointment?.recurrence_id) {
      await updateAppointment.mutateAsync({ id: derivedAppointmentId, input });
      return;
    }

    const recurrenceId = initialAppointment.recurrence_id;
    const recurrenceFrequency = (data.recurrence_frequency ?? "weekly") as RecurrenceFrequency;
    const recurrenceInterval = data.recurrence_interval ?? 1;
    const recurrenceWeekdays = (data.recurrence_weekdays && data.recurrence_weekdays.length > 0)
      ? sortNumberArray(data.recurrence_weekdays)
      : [startDateTime.getDay()];
    const recurrenceEndType = data.recurrence_end_type ?? "date";
    const recurrenceEndsOn = recurrenceEndType === "date" && data.recurrence_ends_on
      ? toLocalDateIso(data.recurrence_ends_on)
      : null;
    const recurrenceMaxOccurrences = recurrenceEndType === "count" ? (data.recurrence_max_occurrences ?? null) : null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: recurrenceError } = await (supabase as any)
      .from("recurrence")
      .update({
        owner_id: data.owner_id,
        vet_user_id: data.vet_user_id && data.vet_user_id.trim() !== "" ? data.vet_user_id : null,
        type: data.type,
        notes: data.notes?.trim() ? data.notes : null,
        is_taxi_dog: data.is_taxi_dog ?? false,
        taxi_dog_type: data.is_taxi_dog ? (data.taxi_dog_type ?? DEFAULT_APPOINTMENT_DRIVER_TYPE) : null,
        frequency: recurrenceFrequency,
        interval: recurrenceInterval,
        weekdays: recurrenceFrequency === "weekly" ? recurrenceWeekdays : null,
        monthday: recurrenceFrequency === "monthly" ? startDateTime.getDate() : null,
        starts_on: toLocalDateIso(startDateTime),
        ends_on: recurrenceEndsOn,
        max_occurrences: recurrenceMaxOccurrences,
      })
      .eq("id", recurrenceId);
    if (recurrenceError) throw recurrenceError;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: futureAppointments, error: futureError } = await (supabase as any)
      .from("appointments")
      .select("id, starts_at")
      .eq("recurrence_id", recurrenceId)
      .gte("starts_at", initialAppointment.starts_at)
      .is("deleted_at", null)
      .order("starts_at", { ascending: true });
    if (futureError) throw futureError;

    const rows = (futureAppointments ?? []) as Array<{ id: string; starts_at: string }>;
    if (rows.length === 0) {
      await updateAppointment.mutateAsync({ id: derivedAppointmentId, input });
      return;
    }

    const {
      pet_ids: petIdsForFuture,
      ...appointmentUpdateFields
    } = input as Parameters<typeof updateAppointment.mutateAsync>[0]["input"] & { pet_ids?: string[] };

    // Reaplica os pets (mesmo conjunto) nas ocorrências alvo, quando informado.
    const applyPetRelationsToRows = async (appointmentIds: string[]) => {
      if (!Array.isArray(petIdsForFuture) || petIdsForFuture.length === 0) return;
      if (appointmentIds.length === 0) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: deletePetsError } = await (supabase as any)
        .from("appointment_pets")
        .delete()
        .in("appointment_id", appointmentIds);
      if (deletePetsError) throw deletePetsError;

      const relationRows = appointmentIds.flatMap((appointmentId) =>
        petIdsForFuture.map((petId) => ({ appointment_id: appointmentId, pet_id: petId })),
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: insertPetsError } = await (supabase as any)
        .from("appointment_pets")
        .insert(relationRows);
      if (insertPetsError) throw insertPetsError;
    };

    // CAMINHO 1 — o padrão de recorrência NÃO mudou (dia da semana, frequência,
    // intervalo e término são os mesmos). Só variam horário e/ou dados avulsos.
    // Ajustamos as ocorrências que já existem, mantendo a DATA de cada uma e
    // trocando apenas o horário do dia. Nada é criado nem desvinculado — é isto
    // que elimina as duplicatas e os agendamentos órfãos.
    if (!hasRecurringRuleChanges(data)) {
      const durationMs = Math.max(0, endDateTime.getTime() - startDateTime.getTime());
      const newHours = startDateTime.getHours();
      const newMinutes = startDateTime.getMinutes();

      const {
        starts_at: _editedStartsAt,
        ends_at: _editedEndsAt,
        ...fieldUpdatesWithoutSchedule
      } = appointmentUpdateFields as typeof appointmentUpdateFields & {
        starts_at?: string;
        ends_at?: string;
      };

      for (const row of rows) {
        const rowStart = new Date(row.starts_at);
        rowStart.setHours(newHours, newMinutes, 0, 0);
        const rowEnd = new Date(rowStart.getTime() + durationMs);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from("appointments")
          .update({
            ...fieldUpdatesWithoutSchedule,
            starts_at: rowStart.toISOString(),
            ends_at: rowEnd.toISOString(),
          })
          .eq("id", row.id);
        if (error) throw error;
      }

      await applyPetRelationsToRows(rows.map((row) => row.id));

      await queryClient.invalidateQueries({ queryKey: ["appointments"] });
      await queryClient.invalidateQueries({ queryKey: ["appointment", derivedAppointmentId] });
      await queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
      return;
    }

    // CAMINHO 2 — o padrão de recorrência mudou. É preciso reconciliar a série:
    // reposicionar as ocorrências existentes segundo a nova regra e criar/remover
    // as que sobram ou faltam.
    const generated = generateRecurrenceOccurrences(startDateTime, endDateTime, {
      frequency: recurrenceFrequency,
      interval: recurrenceInterval,
      weekdays: recurrenceFrequency === "weekly" ? recurrenceWeekdays : undefined,
      monthday: recurrenceFrequency === "monthly" ? startDateTime.getDate() : null,
      endsOn: recurrenceEndsOn ? parseLocalDateIso(recurrenceEndsOn) : null,
      maxOccurrences: recurrenceMaxOccurrences,
      windowDays: recurrenceEndType === "none" ? 365 : 730,
    });
    if (generated.length === 0) {
      throw new Error("A regra de recorrência não gerou datas futuras.");
    }

    const iterations = Math.min(rows.length, generated.length);
    for (let i = 0; i < iterations; i += 1) {
      const row = rows[i];
      const occurrence = generated[i];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("appointments")
        .update({
          ...appointmentUpdateFields,
          starts_at: occurrence.startsAt.toISOString(),
          ends_at: occurrence.endsAt.toISOString(),
          recurrence_occurrence_date: occurrence.occurrenceDate,
        })
        .eq("id", row.id);
      if (error) throw error;
    }

    if (Array.isArray(petIdsForFuture) && petIdsForFuture.length > 0) {
      const targetAppointmentIds = rows.slice(0, iterations).map((row) => row.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: deletePetsError } = await (supabase as any)
        .from("appointment_pets")
        .delete()
        .in("appointment_id", targetAppointmentIds);
      if (deletePetsError) throw deletePetsError;

      const relationRows = targetAppointmentIds.flatMap((appointmentId) =>
        petIdsForFuture.map((petId) => ({
          appointment_id: appointmentId,
          pet_id: petId,
        })),
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: insertPetsError } = await (supabase as any)
        .from("appointment_pets")
        .insert(relationRows);
      if (insertPetsError) throw insertPetsError;
    }

    // Create missing future occurrences when new rule expands the series.
    if (generated.length > rows.length) {
      const extraOccurrences = generated.slice(rows.length);
      const { data: auth } = await supabase.auth.getUser();
      const actorId = auth.user?.id;
      if (!actorId) throw new Error("Usuário não autenticado");

      const primaryPetId = Array.isArray(petIdsForFuture) && petIdsForFuture.length > 0
        ? petIdsForFuture[0]
        : data.pet_ids[0];
      if (!primaryPetId) throw new Error("Selecione ao menos um paciente");

      const payload = extraOccurrences.map((occ) => ({
        ...appointmentUpdateFields,
        clinic_id: data.clinic_id,
        owner_id: data.owner_id,
        pet_id: primaryPetId,
        created_by_user_id: actorId,
        recurrence_id: recurrenceId,
        recurrence_occurrence_date: occ.occurrenceDate,
        is_recurrence_exception: false,
        starts_at: occ.startsAt.toISOString(),
        ends_at: occ.endsAt.toISOString(),
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: createdRows, error: createError } = await (supabase as any)
        .from("appointments")
        .insert(payload)
        .select("id");
      if (createError) throw createError;

      const createdIds = (createdRows ?? []).map((row: { id: string }) => row.id);
      const relationPetIds = Array.isArray(petIdsForFuture) && petIdsForFuture.length > 0
        ? petIdsForFuture
        : data.pet_ids;
      const relationRows = createdIds.flatMap((appointmentId) =>
        relationPetIds.map((petId) => ({ appointment_id: appointmentId, pet_id: petId })),
      );
      if (relationRows.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: relationsError } = await (supabase as any)
          .from("appointment_pets")
          .insert(relationRows);
        if (relationsError) throw relationsError;
      }
    }

    // Handle overflow when edited rule shrinks the series.
    if (rows.length > generated.length) {
      const overflowRows = rows.slice(generated.length);
      if (overflowMode === "delete") {
        for (const row of overflowRows) {
          const { error } = await supabase.rpc("soft_delete_appointment", {
            p_appointment_id: row.id,
          });
          if (error) throw error;
        }
      } else {
        const overflowIds = overflowRows.map((row) => row.id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from("appointments")
          .update({
            recurrence_id: null,
            recurrence_occurrence_date: null,
            is_recurrence_exception: true,
          })
          .in("id", overflowIds);
        if (error) throw error;
      }
    }

    await queryClient.invalidateQueries({ queryKey: ["appointments"] });
    await queryClient.invalidateQueries({ queryKey: ["appointment", derivedAppointmentId] });
    await queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
  }, [derivedAppointmentId, hasRecurringRuleChanges, initialAppointment?.recurrence_id, initialAppointment?.starts_at, queryClient, updateAppointment]);

  const getFutureOverflowCount = useCallback(async (data: AppointmentFormData) => {
    if (!initialAppointment?.recurrence_id) return 0;
    const startDateTime = new Date(data.date);
    const [startHour, startMinute] = data.start_time.split(":");
    startDateTime.setHours(parseInt(startHour, 10), parseInt(startMinute, 10), 0, 0);
    const endDateTime = new Date(data.date);
    const [endHour, endMinute] = data.end_time.split(":");
    endDateTime.setHours(parseInt(endHour, 10), parseInt(endMinute, 10), 0, 0);
    if (endDateTime.getTime() <= startDateTime.getTime()) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }

    const recurrenceFrequency = (data.recurrence_frequency ?? "weekly") as RecurrenceFrequency;
    const recurrenceInterval = data.recurrence_interval ?? 1;
    const recurrenceWeekdays = (data.recurrence_weekdays && data.recurrence_weekdays.length > 0)
      ? sortNumberArray(data.recurrence_weekdays)
      : [startDateTime.getDay()];
    const recurrenceEndType = data.recurrence_end_type ?? "date";
    const recurrenceEndsOn = recurrenceEndType === "date" && data.recurrence_ends_on
      ? toLocalDateIso(data.recurrence_ends_on)
      : null;
    const recurrenceMaxOccurrences = recurrenceEndType === "count" ? (data.recurrence_max_occurrences ?? null) : null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: futureAppointments, error: futureError } = await (supabase as any)
      .from("appointments")
      .select("id")
      .eq("recurrence_id", initialAppointment.recurrence_id)
      .gte("starts_at", initialAppointment.starts_at)
      .is("deleted_at", null);
    if (futureError) throw futureError;
    const existingCount = (futureAppointments ?? []).length;

    const generated = generateRecurrenceOccurrences(startDateTime, endDateTime, {
      frequency: recurrenceFrequency,
      interval: recurrenceInterval,
      weekdays: recurrenceFrequency === "weekly" ? recurrenceWeekdays : undefined,
      monthday: recurrenceFrequency === "monthly" ? startDateTime.getDate() : null,
      endsOn: recurrenceEndsOn ? parseLocalDateIso(recurrenceEndsOn) : null,
      maxOccurrences: recurrenceMaxOccurrences,
      windowDays: recurrenceEndType === "none" ? 365 : 730,
    });
    return Math.max(0, existingCount - generated.length);
  }, [initialAppointment?.recurrence_id, initialAppointment?.starts_at]);

  const hasFollowingRecurrenceOccurrences = useCallback(async () => {
    if (!initialAppointment?.recurrence_id || !initialAppointment?.starts_at) return false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: followingRows, error } = await (supabase as any)
      .from("appointments")
      .select("id")
      .eq("recurrence_id", initialAppointment.recurrence_id)
      .gt("starts_at", initialAppointment.starts_at)
      .is("deleted_at", null)
      .limit(1);

    if (error) throw error;
    return (followingRows ?? []).length > 0;
  }, [initialAppointment?.recurrence_id, initialAppointment?.starts_at]);

  const executeSubmit = async (
    data: AppointmentFormData,
    recurrenceScope?: "single" | "future",
    overflowMode: "keep" | "delete" = "keep",
  ) => {
    const startDateTime = new Date(data.date);
    const [startHour, startMinute] = data.start_time.split(":");
    startDateTime.setHours(parseInt(startHour), parseInt(startMinute));

    const endDateTime = new Date(data.date);
    const [endHour, endMinute] = data.end_time.split(":");
    endDateTime.setHours(parseInt(endHour), parseInt(endMinute));

    // Se horário final <= inicial, considerar término no dia seguinte (ex.: 22:00–02:00)
    if (endDateTime.getTime() <= startDateTime.getTime()) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }

    const serviceItemsPayload = serviceItems.map((item) => ({
      item_id: item.itemId ?? undefined,
      title_snapshot: item.title,
      unit_price_snapshot: item.unitPrice,
      qty: item.quantity,
    }));
    const appointmentItemsPayload: AppointmentItemInput[] = serviceItems
      .filter((item) => item.itemId)
      .map((item) => ({
        item_id: item.itemId!,
        title_snapshot: item.title,
        qty: item.quantity,
      }));

    try {
      const selectedGroomingStatusConfig =
        selectedBehavior === BEHAVIOR_BANHO_TOSA
          ? getStatusConfigByName(String(data.grooming_status ?? initialGroomingStatus ?? firstGroomingStatusName), groomingStatuses)
          : undefined;
      const selectedGroomingStatus =
        (data.grooming_status ?? initialGroomingStatus ?? firstGroomingStatusName) as GroomingStatus;
      // `grooming_status` is unified into `appointments.status`, so the selected
      // grooming status name IS the status to persist. Do NOT coerce it (that would
      // flatten no_banho/na_tosa/secando to em_atendimento when only the date/time
      // of a banho_tosa appointment changes).
      const mappedGroomingAppointmentStatus = resolveGroomingAppointmentStatus(
        selectedGroomingStatus,
        selectedGroomingStatusConfig?.final_type,
      );

      if (mode === "edit") {
        if (!derivedAppointmentId) return;

        const newStatus =
          selectedBehavior === BEHAVIOR_BANHO_TOSA
            ? mappedGroomingAppointmentStatus
            : coerceAppointmentStatusForNonGrooming(data.status ?? initialAppointmentStatus ?? firstAppointmentStatusName, firstAppointmentStatusName);

        const input = {
          clinic_id: data.clinic_id,
          vet_user_id: data.vet_user_id && data.vet_user_id.trim() !== "" ? data.vet_user_id : null,
          owner_id: data.owner_id,
          pet_ids: data.pet_ids,
          type: data.type,
          status: newStatus,
          starts_at: startDateTime.toISOString(),
          ends_at: endDateTime.toISOString(),
          notes: data.notes?.trim() ? data.notes : null,
          is_taxi_dog: data.is_taxi_dog ?? false,
          taxi_dog_type: data.is_taxi_dog ? (data.taxi_dog_type ?? DEFAULT_APPOINTMENT_DRIVER_TYPE) : null,
        } as Parameters<typeof updateAppointment.mutateAsync>[0]["input"];

        const shouldHandleRecurrence =
          Boolean(initialAppointment?.recurrence_id) &&
          (recurrenceScope === "future" || hasRecurringRuleChanges(data));

        if (shouldHandleRecurrence && recurrenceScope === "future") {
          await performUpdateRecurringFuture(data, startDateTime, endDateTime, input, overflowMode);
        } else {
          await updateAppointment.mutateAsync({
            id: derivedAppointmentId,
            input,
          });
        }

        await persistAppointmentPackageLinks(derivedAppointmentId, newStatus === "finalizado");

        onSuccess?.();
        return;
      }

      const targetAppointmentStatus =
        selectedBehavior === BEHAVIOR_BANHO_TOSA
          ? mappedGroomingAppointmentStatus
          : coerceAppointmentStatusForNonGrooming(data.status ?? initialAppointmentStatus ?? firstAppointmentStatusName, firstAppointmentStatusName);

      const payload: AppointmentInput = {
        clinic_id: data.clinic_id,
        vet_user_id: data.vet_user_id && data.vet_user_id.trim() !== "" ? data.vet_user_id : null,
        owner_id: data.owner_id,
        pet_ids: data.pet_ids,
        type: data.type,
        starts_at: startDateTime.toISOString(),
        ends_at: endDateTime.toISOString(),
        notes: data.notes,
        is_taxi_dog: data.is_taxi_dog ?? false,
        taxi_dog_type: data.is_taxi_dog ? (data.taxi_dog_type ?? DEFAULT_APPOINTMENT_DRIVER_TYPE) : null,
        service_items: serviceItemsPayload,
        status: targetAppointmentStatus,
      };
      if (data.is_recurring) {
        const weeklyDays =
          (data.recurrence_weekdays && data.recurrence_weekdays.length > 0
            ? data.recurrence_weekdays
            : [startDateTime.getDay()]);
        const recurrenceEndType = data.recurrence_end_type ?? "date";
        await createRecurringAppointments.mutateAsync({
          appointment: payload,
          recurrence: {
            frequency: (data.recurrence_frequency ?? "weekly") as RecurrenceFrequency,
            interval: data.recurrence_interval ?? 1,
            weekdays: (data.recurrence_frequency ?? "weekly") === "weekly" ? weeklyDays : undefined,
            monthday: (data.recurrence_frequency ?? "weekly") === "monthly" ? startDateTime.getDate() : null,
            ends_on: recurrenceEndType === "date" && data.recurrence_ends_on
              ? toLocalDateIso(data.recurrence_ends_on)
              : null,
            max_occurrences: recurrenceEndType === "count" ? (data.recurrence_max_occurrences ?? null) : null,
            timezone: "America/Sao_Paulo",
            window_days: recurrenceEndType === "none" ? 365 : 730,
          },
        });
      } else {
        const createdAppointment = await createAppointment.mutateAsync(payload);
        await persistAppointmentPackageLinks(
          createdAppointment.id,
          payload.status === "finalizado",
        );
      }

      onSuccess?.();
      resetFormState();
    } catch (error) {
      toast({
        title: "Erro ao salvar agendamento",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const onSubmit = async (data: AppointmentFormData) => {
    const selectedPetsForSubmit = selectedPets.length > 0
      ? selectedPets
      : data.pet_ids
        .map((petId) => ownerPets.find((pet) => pet.id === petId))
        .filter((pet): pet is PetWithOwner => Boolean(pet));
    const hasDeceasedAppointment =
      selectedPetsForSubmit.some((pet) => !canCreateFutureAppointmentForPet(pet)) ||
      data.pet_ids.some((petId) => {
        const snapshot = selectedPetDeathSnapshot[petId];
        if (!snapshot) return false;
        return isPetDeceased({ status: { name: snapshot.statusName }, death_date: snapshot.deathDate });
      });

    if (hasDeceasedAppointment) {
      setPendingDeceasedSubmitData(data);
      setIsDeceasedSubmitDialogOpen(true);
      return;
    }

    if (mode === "edit" && initialAppointment?.recurrence_id) {
      try {
        const hasFollowing = await hasFollowingRecurrenceOccurrences();
        if (!hasFollowing) {
          await executeSubmit(data, "single");
          return;
        }
      } catch (error) {
        toast({
          title: "Erro ao verificar recorrência",
          description: error instanceof Error ? error.message : "Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      setPendingRecurringSubmitData(data);
      setIsRecurrenceScopeDialogOpen(true);
      return;
    }
    await executeSubmit(data);
  };

  const handleDeleteAppointment = async (scope: "single" | "future" = "single") => {
    if (mode !== "edit") return;
    if (!derivedAppointmentId) return;
    try {
      if (scope === "future" && initialAppointment?.recurrence_id) {
        setIsDeletingRecurringFuture(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: futureAppointments, error: futureError } = await (supabase as any)
          .from("appointments")
          .select("id")
          .eq("recurrence_id", initialAppointment.recurrence_id)
          .gte("starts_at", initialAppointment.starts_at)
          .is("deleted_at", null)
          .order("starts_at", { ascending: true });
        if (futureError) throw futureError;

        const ids = (futureAppointments ?? []).map((row: { id: string }) => row.id);
        for (const id of ids) {
          const { error } = await supabase.rpc("soft_delete_appointment", {
            p_appointment_id: id,
          });
          if (error) throw error;
        }

        await queryClient.invalidateQueries({ queryKey: ["appointments"] });
        await queryClient.invalidateQueries({ queryKey: ["appointment", derivedAppointmentId] });
        await queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
        toast({
          title: "Ocorrências futuras removidas com sucesso!",
        });
      } else {
        await deleteAppointment.mutateAsync(derivedAppointmentId);
      }
      setIsDeleteDialogOpen(false);
      setIsDeleteScopeDialogOpen(false);
      onDeleteSuccess?.();
    } catch (error) {
      if (scope === "future") {
        toast({
          title: "Erro ao remover agendamento",
          description: error instanceof Error ? error.message : "Tente novamente.",
          variant: "destructive",
        });
      }
    } finally {
      if (scope === "future") {
        setIsDeletingRecurringFuture(false);
      }
    }
  };

  const openOwnerAndPetRegister = () => {
    setRegisterMode("owner_and_pet");
    setIsRegisterDialogOpen(true);
  };

  const openPetOnlyRegister = () => {
    if (!selectedOwnerId) return;
    setRegisterMode("pet_only");
    setIsRegisterDialogOpen(true);
  };

  return (
    <>
      <div className={mobileLayout ? "flex min-h-0 flex-1 flex-col" : "flex max-h-[90vh] flex-col"}>
        {!mobileLayout && (
          <div className="shrink-0 border-b border-border/60 bg-white px-6 py-4">
            <DialogHeader className="text-left">
              <DialogTitle className="text-2xl font-semibold">
                {mode === "edit" ? t("calendar:appointmentForm.editTitle") : t("calendar:appointmentForm.createTitle")}
              </DialogTitle>
            </DialogHeader>
          </div>
        )}

        {clinicId ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
              <div className={`min-h-0 flex-1 overflow-x-hidden overflow-y-auto ${mobileLayout ? "p-4" : "p-6"}`}>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr,420px]">
                  {/* Coluna esquerda: detalhes */}
                  <div className="space-y-4">
                    <section className="rounded-lg border border-border/60 bg-white p-4">
                      <div className=" grid gap-4 md:grid-cols-2">
                        {canSelectClinic ? (
                          <FormField
                            control={form.control}
                            name="clinic_id"
                            render={({ field }) => (
                              <FormItem className="md:col-span-2">
                                <FormLabel>{t("calendar:multiClinic.clinicFieldLabel")}</FormLabel>
                                <Select
                                  value={field.value ?? ""}
                                  onValueChange={(value) => {
                                    field.onChange(value);
                                    setSelectedClinicId(value);
                                    // Veterinários são por clínica: limpa a seleção anterior.
                                    form.setValue("vet_user_id", "", { shouldDirty: true });
                                    setVetDisplayValue("");
                                  }}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {visibleClinicIds.map((id) => (
                                      <SelectItem key={id} value={id}>
                                        {clinicsById[id]?.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                  {t("calendar:multiClinic.clinicFieldHelp")}
                                </p>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ) : null}
                        <FormField
                          control={form.control}
                          name="type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("calendar:appointmentForm.appointmentType")} <span className="text-destructive">*</span></FormLabel>
                                {resolvedPrefilledType && BOARDING_APPOINTMENT_TYPES.includes(resolvedPrefilledType) ? (
                                    <FormControl>
                                      <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                                        {resolvedPrefilledType && t(`calendar:type${resolvedPrefilledType.charAt(0).toUpperCase()}${resolvedPrefilledType.slice(1)}`)}
                                        <input type="hidden" {...field} />
                                      </div>
                                    </FormControl>
                                  ) : (
                                  <Select
                                    onValueChange={(value) => {
                                      field.onChange(value);
                                      const start = form.getValues("start_time");
                                      if (start) {
                                        form.setValue(
                                          "end_time",
                                          addMinutesToTimeString(
                                            start,
                                            resolveDefaultDurationMinutes(clinicTypes.find(t => t.id === value)?.behavior, durationMinutesByType),
                                          ),
                                          { shouldDirty: true, shouldValidate: true },
                                        );
                                      }
                                    }}
                                    value={field.value ?? ""}
                                  >
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder={t("calendar:appointmentForm.selectType")} />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {clinicTypes.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-muted-foreground">
                                          Nenhum tipo configurado
                                        </div>
                                      ) : (
                                        clinicTypes.map((ct) => (
                                          <SelectItem key={ct.id} value={ct.id}>
                                            <span className="flex items-center gap-2">
                                              {renderIconBySlug(ct.icon, { sizeClass: "h-4 w-4", tone: "current" })}
                                              <span style={{ color: ct.color }}>■</span>
                                              {ct.name}
                                            </span>
                                          </SelectItem>
                                        ))
                                      )}
                                    </SelectContent>
                                  </Select>
                                )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="date"
                          render={({ field }) => {
                            const dateError = getDateErrorMessage(dateInput);

                            return (
                              <FormItem >
                                <FormLabel>{t("calendar:appointmentForm.dateLabel")} <span className="text-destructive">*</span></FormLabel>
                                <FormControl>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      placeholder={t("calendar:appointmentForm.datePlaceholder")}
                                      inputMode="numeric"
                                      ref={dateInputRef}
                                      value={dateInput}
                                      onChange={(event) => {
                                        const rawValue = event.target.value;
                                        captureDateCaret(rawValue, event.target.selectionStart);

                                        const maskedValue = maskDateInput(rawValue);
                                        setDateInput(maskedValue);
                                        const isoValue = convertDisplayToISO(maskedValue);
                                        if (isoValue) {
                                          const dateObj = parseLocalDateIso(isoValue);
                                          field.onChange(dateObj ?? undefined);
                                        } else {
                                          field.onChange(undefined);
                                        }
                                      }}
                                      onBlur={() => {
                                        const isoValue = convertDisplayToISO(dateInput);
                                        if (!isoValue) {
                                          field.onChange(undefined);
                                          setDateInput("");
                                        }
                                      }}
                                      className={cn("flex-1", dateError && "border-destructive")}
                                    />
                                    <DateFieldPopover
                                      open={isDateCalendarOpen}
                                      onOpenChange={setIsDateCalendarOpen}
                                      trigger={
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="icon"
                                          aria-label={t("calendar:appointmentForm.selectDate")}
                                          className="!w-10 !h-10"
                                        >
                                          <CalendarIcon className="h-4 w-4" />
                                        </Button>
                                      }
                                      selected={field.value}
                                      onSelect={(date) => {
                                        if (date) {
                                          const iso = toLocalDateIso(date);
                                          field.onChange(date);
                                          setDateInput(formatISOToDisplay(iso));
                                        }
                                      }}
                                    />
                                  </div>
                                </FormControl>
                                {dateError && (
                                  <p className="text-sm text-destructive">{dateError}</p>
                                )}
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />

                        <FormField
                          control={form.control}
                          name="start_time"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("calendar:appointmentForm.startTime")} <span className="text-destructive">*</span></FormLabel>
                              <FormControl>
                                <Input
                                  type="time"
                                  {...field}
                                  onChange={(event) => {
                                    field.onChange(event);
                                    const nextStart = event.target.value;
                                    const apptTypeId = form.getValues("type");
                                    if (nextStart && apptTypeId) {
                                      form.setValue(
                                        "end_time",
                                        addMinutesToTimeString(
                                          nextStart,
                                          resolveDefaultDurationMinutes(clinicTypes.find(t => t.id === apptTypeId)?.behavior, durationMinutesByType),
                                        ),
                                        { shouldDirty: true, shouldValidate: true },
                                      );
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="end_time"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("calendar:appointmentForm.endTime")} <span className="text-destructive">*</span></FormLabel>
                              <FormControl>
                                <Input type="time" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="is_recurring"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 md:col-span-2">
                              <FormControl>
                                <Checkbox
                                  checked={field.value ?? false}
                                  onCheckedChange={field.onChange}
                                  aria-label={t("calendar:appointmentForm.recurring")}
                                  disabled={mode === "edit"}
                                />
                              </FormControl>
                              <div className="space-y-0.5 leading-none">
                                <FormLabel className="cursor-pointer font-normal">{t("calendar:appointmentForm.recurring")}</FormLabel>
                                {mode === "edit" || isRecurring && (
                                  <p className="text-xs text-muted-foreground">
                                    {t("calendar:appointmentForm.recurringSummaryHint")}
                                  </p>
                                )}
                              </div>
                            </FormItem>
                          )}
                        />

                        {(mode !== "edit" ? isRecurring : Boolean(initialAppointment?.recurrence_id)) ? (
                          <>
                            <FormField
                              control={form.control}
                              name="recurrence_frequency"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Frequência</FormLabel>
                                  <Select onValueChange={field.onChange} value={field.value ?? "weekly"}>
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="weekly">Semanal</SelectItem>
                                      <SelectItem value="monthly">Mensal</SelectItem>
                                      <SelectItem value="yearly">Anual</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name="recurrence_interval"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Intervalo</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      min={1}
                                      value={field.value == null ? "" : String(field.value)}
                                      onChange={(event) => {
                                        const rawValue = event.target.value;

                                        if (rawValue === "") {
                                          field.onChange(null);
                                          return;
                                        }

                                        const value = Number(rawValue);
                                        if (Number.isNaN(value)) return;
                                        field.onChange(Math.max(1, Math.floor(value)));
                                      }}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {recurrenceFrequency === "weekly" ? (
                              <FormField
                                control={form.control}
                                name="recurrence_weekdays"
                                render={({ field }) => {
                                  const selected = field.value ?? [];
                                  return (
                                    <FormItem className="md:col-span-2">
                                      <FormLabel>Dias da semana</FormLabel>
                                      <div className="flex flex-wrap gap-2">
                                        {WEEKDAY_OPTIONS.map((option) => {
                                          const checked = selected.includes(option.value);
                                          return (
                                            <Button
                                              key={option.value}
                                              type="button"
                                              variant={checked ? "default" : "outline"}
                                              size="sm"
                                              onClick={() => {
                                                const next = checked
                                                  ? selected.filter((value) => value !== option.value)
                                                  : [...selected, option.value];
                                                field.onChange(next);
                                              }}
                                            >
                                              {option.label}
                                            </Button>
                                          );
                                        })}
                                      </div>
                                    </FormItem>
                                  );
                                }}
                              />
                            ) : null}

                            <FormField
                              control={form.control}
                              name="recurrence_end_type"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Prazo de recorrência</FormLabel>
                                  <Select onValueChange={field.onChange} value={field.value ?? "date"}>
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="none">Durante 1 ano</SelectItem>
                                      <SelectItem value="date">Até uma data</SelectItem>
                                      <SelectItem value="count">Qtd. de ocorrências</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </FormItem>
                              )}
                            />

                            {recurrenceEndType === "date" ? (
                              <FormField
                                control={form.control}
                                name="recurrence_ends_on"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Data final</FormLabel>
                                    <FormControl>
                                      <div className="flex items-center gap-2">
                                        <Input
                                          placeholder="DD/MM/AAAA"
                                          inputMode="numeric"
                                          className="!flex-1"
                                          ref={recurrenceEndDateInputRef}
                                          value={recurrenceEndDateInput}
                                          onChange={(event) => {
                                            const rawValue = event.target.value;
                                            captureRecurrenceEndDateCaret(rawValue, event.target.selectionStart);
                                            const maskedValue = maskDateInput(rawValue);
                                            setRecurrenceEndDateInput(maskedValue);
                                            const isoValue = convertDisplayToISO(maskedValue);
                                            if (isoValue) {
                                              const dateObj = parseLocalDateIso(isoValue);
                                              field.onChange(dateObj ?? undefined);
                                            } else {
                                              field.onChange(undefined);
                                            }
                                          }}
                                          onBlur={() => {
                                            const isoValue = convertDisplayToISO(recurrenceEndDateInput);
                                            if (!isoValue) {
                                              field.onChange(undefined);
                                              setRecurrenceEndDateInput("");
                                            }
                                          }}
                                        />
                                        <DateFieldPopover
                                          open={isRecurrenceEndCalendarOpen}
                                          onOpenChange={setIsRecurrenceEndCalendarOpen}
                                          trigger={
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="icon"
                                              className="!w-10 !h-10"
                                              aria-label="Selecionar data final da recorrência"
                                            >
                                              <CalendarIcon className="h-4 w-4" />
                                            </Button>
                                          }
                                          selected={field.value}
                                          onSelect={(date) => {
                                            if (!date) return;
                                            const iso = toLocalDateIso(date);
                                            field.onChange(date);
                                            setRecurrenceEndDateInput(formatISOToDisplay(iso));
                                          }}
                                        />
                                      </div>
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            ) : null}

                            {recurrenceEndType === "count" ? (
                              <FormField
                                control={form.control}
                                name="recurrence_max_occurrences"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Quantidade</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min={1}
                                        value={field.value ?? 1}
                                        onChange={(event) => {
                                          const value = Number(event.target.value);
                                          field.onChange(Number.isNaN(value) ? 1 : Math.max(1, Math.floor(value)));
                                        }}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            ) : null}
                          </>
                        ) : null}

                        <FormField
                          control={form.control}
                          name="notes"
                          render={({ field }) => (
                            <FormItem className="flex flex-col md:col-span-2 mt-2">
                              <FormLabel>{t("calendar:appointmentForm.notes")}</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder={t("calendar:appointmentForm.notesPlaceholder")}
                                  className="min-h-[140px] resize-none"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="is_taxi_dog"
                          render={({ field }) => (
                            <FormItem className="space-y-2 md:col-span-2">
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                <div className="flex flex-row items-start space-x-3 space-y-0 mb-2">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value ?? false}
                                      onCheckedChange={field.onChange}
                                      aria-label={t("calendar:appointmentForm.addDriver")}
                                    />
                                  </FormControl>
                                  <div className="space-y-0.5 leading-none">
                                    <FormLabel className="cursor-pointer font-normal">
                                      {field.value ? t("calendar:appointmentForm.driverType") : t("calendar:appointmentForm.addDriver")}
                                    </FormLabel>
                                  </div>
                                </div>

                                {field.value ? (
                                  <FormField
                                    control={form.control}
                                    name="taxi_dog_type"
                                    render={({ field: typeField }) => (
                                      <FormItem className="w-full sm:max-w-[220px]">
                                        <Select
                                          value={typeField.value ?? DEFAULT_APPOINTMENT_DRIVER_TYPE}
                                          onValueChange={(value) => typeField.onChange(value as AppointmentDriverType)}
                                        >
                                          <FormControl>
                                            <SelectTrigger>
                                              <SelectValue placeholder={t("calendar:appointmentForm.selectDriverType")} />
                                            </SelectTrigger>
                                          </FormControl>
                                          <SelectContent>
                                            {APPOINTMENT_DRIVER_TYPE_OPTIONS.map((option) => (
                                              <SelectItem key={option} value={option}>
                                                {option === "pickup_and_dropoff"
                                                  ? t("calendar:driverTypePickupAndDropoff")
                                                  : option === "pickup"
                                                    ? t("calendar:driverTypePickup")
                                                    : t("calendar:driverTypeDropoff")}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                ) : null}
                              </div>
                            </FormItem>
                          )}
                        />
                      </div>
                    </section>

                    {mode !== "edit" && (
                      <section className="rounded-lg border border-border/60 bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentForm.productsAndServices")}</p>
                        </div>
                        <div className="mt-3 space-y-4 text-sm">
                          {incompatibleSelectedItems.length > 0 ? (
                            <Alert className="border-amber-200 bg-amber-50 text-amber-900">
                              <AlertTriangle className="h-4 w-4" />
                              <AlertTitle>{t("calendar:appointmentForm.compatibilityWarningTitle")}</AlertTitle>
                              <AlertDescription>
                                {t("calendar:appointmentForm.compatibilityWarningDescription", {
                                  names: incompatibleSelectedItems.map((item) => item.title).join(", "),
                                })}
                              </AlertDescription>
                            </Alert>
                          ) : null}
                          <div className="rounded-lg border border-dashed bg-muted/30 p-4">
                            <div className="grid gap-3 sm:grid-cols-[1.5fr,0.7fr] sm:items-end">
                              <Autocomplete
                                options={serviceAutocompleteOptions}
                                value={serviceDisplayValue}
                                contentWidth="content"
                                popoverPortalled={false}
                                onChange={(value) => {
                                  setServiceDisplayValue(value);
                                  const matchedItem = unifiedItems.find(
                                    (item) => item.name.toLowerCase() === value.toLowerCase(),
                                  );
                                  if (matchedItem) {
                                    setSelectedItemId(matchedItem.id);
                                  } else {
                                    setSelectedItemId("");
                                  }
                                }}
                                onSelect={(option) => {
                                  setSelectedItemId(option.value);
                                  setServiceDisplayValue(option.label);
                                  refetchItems();
                                }}
                                selectedValue={selectedItemId}
                                placeholder={t("calendar:appointmentForm.itemPlaceholder")}
                                emptyMessage={t("calendar:appointmentForm.noItemResults")}
                                renderOption={(option, isSelected) => {
                                  const item = unifiedItems.find((i) => i.id === option.value);

                                  if (!item) return option.label;

                                  return (
                                    <>
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4 shrink-0",
                                          isSelected ? "opacity-100" : "opacity-0",
                                        )}
                                      />
                                      <span className="min-w-0 max-w-[200px] truncate md:max-w-[400px] flex-1 text-left">
                                        {option.label}
                                      </span>
                                      <Badge className={cn("h-6 shrink-0 text-xs", getItemTypeBadgeClass(item.type))}>
                                        {getItemTypeLabel(item)}
                                      </Badge>
                                    </>
                                  );
                                }}
                              />
                              <Input
                                type="number"
                                min={1}
                                value={serviceQuantity}
                                onChange={(event) => {
                                  const value = Number(event.target.value);
                                  setServiceQuantity(Number.isNaN(value) ? 1 : Math.max(1, value));
                                }}
                              />
                            </div>
                            <Button
                              type="button"
                              className="mt-3 w-full"
                              onClick={handleAddService}
                              disabled={!selectedItem}
                            >
                              <PlusCircle className="mr-2 h-4 w-4" />
                              {t("calendar:appointmentForm.add")}
                            </Button>
                            {selectedItem && (
                              <p className="mt-3 text-xs text-muted-foreground">
                                {t("calendar:appointmentForm.unitPrice")}: {formatCurrency(selectedItem.price)} · {t("calendar:appointmentForm.estimatedTotal")}:{" "}
                                {formatCurrency(selectedItem.price * serviceQuantity)}
                              </p>
                            )}
                          </div>

                          <Separator />

                          <div className="space-y-3">
                            {serviceItems.length > 0 ? (
                              serviceItems.map((item) => {
                                const itemKey = item._key;
                                return (
                                  <div
                                    key={itemKey}
                                    className="flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                                  >
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className="font-semibold leading-none">{item.title}</p>
                                        {(item.type || item.isVaccine) && getItemTypeBadge(item)}
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        {formatCurrency(item.unitPrice)} cada
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <Input
                                        type="number"
                                        min={1}
                                        value={item.quantity}
                                        onChange={(event) =>
                                          updateServiceQuantity(
                                            itemKey,
                                            Number(event.target.value),
                                          )
                                        }
                                        className="w-20"
                                        disabled={isAppointmentPaid}
                                      />
                                      <span className="text-sm font-medium">
                                        {formatCurrency(item.unitPrice * item.quantity)}
                                      </span>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeService(itemKey)}
                                        disabled={isAppointmentPaid}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })
                            ) : null}
                          </div>

                        </div>
                      </section>
                    )}

                    {mode === "create" ? (
                      <AppointmentPackageSection
                        clinicId={clinicId}
                        petId={activePetId}
                        values={packageDrafts}
                        appointmentType={selectedType}
                        disabled={isPackageSectionLocked}
                        isRecurring={isRecurring}
                        onChange={setPackageDrafts}
                      />
                    ) : null}
                  </div>

                  {/* Coluna direita: quem + resumo */}
                  <div className="space-y-4">
                    <AppointmentStatusSidePanel
                      mode={statusSidePanelMode}
                      value={String(statusSidePanelValue)}
                      statuses={sidePanelStatuses}
                      disabled={isSaving}
                      onChange={(next) => {
                        if (statusSidePanelMode === "grooming") {
                          const groomingStatus = next as AppointmentFormGroomingStatus;
                          const selectedStatus = getStatusConfigByName(groomingStatus, groomingStatuses);
                          form.setValue("grooming_status", groomingStatus, { shouldDirty: true, shouldValidate: true });
                          if (selectedStatus?.final_type === "finalizado" || selectedStatus?.final_type === "cancelado") {
                            form.setValue("status", toFormAppointmentStatus(selectedStatus.final_type), {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }
                          return;
                        }
                        form.setValue("status", next as AppointmentFormStatus, { shouldDirty: true, shouldValidate: true });
                      }}
                    />
                    {(hasDeceasedSelectedPet || hasFinancial) ? (
                      <section className="rounded-lg border border-border/60 bg-white p-4">
                        {hasDeceasedSelectedPet ? (
                          <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive">{t("calendar:appointmentForm.deceasedBadge")}</Badge>
                              <span>{t("calendar:appointmentForm.deceasedWarning")}</span>
                            </div>
                            {deceasedSelectedPets.some((pet) => shouldBlockRemindersForPet(pet)) ? (
                              <p className="mt-2 text-muted-foreground">
                                {t("calendar:appointmentForm.deceasedReminderWarning")}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                        {hasFinancial ? (
                          <>
                            <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentForm.payment")}</p>
                            <div className="mt-3 space-y-3 text-sm">
                              <div className="flex items-center justify-between text-muted-foreground">
                                <span>{t("calendar:appointmentForm.productsAndServicesShort")}</span>
                                <span>{formatCurrency(servicesBalanceTotal)}</span>
                              </div>
                              <div className="flex items-center justify-between text-muted-foreground">
                                <span>{t("calendar:packages.title")}</span>
                                <span>{formatCurrency(packageSuggestedChargeTotal)}</span>
                              </div>
                              <div className="flex items-center justify-between rounded-md bg-muted/60 px-3 py-2 font-semibold text-foreground">
                                <span>{t("calendar:appointmentForm.totalToPay")}</span>
                                <span>{formatCurrency(appointmentBalanceTotal)}</span>
                              </div>
                            </div>
                          </>
                        ) : null}
                      </section>
                    ) : null}
                    {(isRecurring || existingRecurrenceSummary) ? (
                      <section className="rounded-lg border border-border/60 bg-white p-4">
                        <p className="text-sm font-semibold text-foreground">{t("calendar:appointmentForm.recurrence")}</p>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {existingRecurrenceSummary ?? recurrenceSummary}
                        </p>
                        {recurrencePreviewDates.length > 0 ? (
                          <div className="mt-3 max-h-[100px] overflow-y-auto rounded-md border border-border/60 p-2">
                            <div className="flex flex-wrap gap-2">
                              {recurrencePreviewDates.map((date, index) => (
                                <Badge key={`${date}-${index}`} variant="secondary">
                                  {date}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </section>
                    ) : null}
                    <section className="rounded-lg border border-border/60 bg-white p-4">
                      <FormField
                        control={form.control}
                        name="vet_user_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("calendar:appointmentForm.responsibleProfessional")}</FormLabel>
                            <FormControl>
                              <Autocomplete
                                options={vetOptions}
                                value={vetDisplayValue}
                                popoverPortalled={false}
                                onChange={(value) => {
                                  setVetDisplayValue(value);
                                  const matchedVet = veterinarians.find(
                                    (vet) => vet.full_name.toLowerCase() === value.toLowerCase(),
                                  );
                                  if (matchedVet) {
                                    field.onChange(matchedVet.id);
                                  } else {
                                    field.onChange("");
                                  }
                                }}
                                onSelect={(option) => {
                                  field.onChange(option.value);
                                }}
                                placeholder={t("calendar:appointmentForm.searchProfessional")}
                                emptyMessage={t("calendar:appointmentForm.noProfessionalResults")}
                                renderOption={(option, isSelected) => {
                                  const vet = veterinarians.find((v) => v.id === option.value) ?? null;
                                  if (!vet) return option.label;

                                  const role = (vet.role ?? "").toLowerCase();
                                  const isVeterinarian = role.includes("veterin");
                                  const isGroomer = role.includes("groomer");

                                  return (
                                    <>
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4 shrink-0 mt-0.5",
                                          isSelected ? "opacity-100" : "opacity-0",
                                        )}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-3">
                                          <p className="font-medium leading-none truncate">{vet.full_name}</p>
                                          <div className="flex flex-wrap items-center justify-end gap-1 shrink-0">
                                            {isVeterinarian && (
                                              <Badge variant="secondary" className="h-5 text-[10px]">
                                                {t("calendar:appointmentForm.veterinarian")}
                                              </Badge>
                                            )}
                                            {isGroomer && (
                                              <Badge variant="secondary" className="h-5 text-[10px]">
                                                Groomer
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </>
                                  );
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </section>
                    <section className="overflow-hidden rounded-lg border border-border/60 bg-white p-4">
                      <div className="space-y-4">
                        {selectedPetIds.length > 0 && !isWhoEditing ? (
                          <div className="space-y-3">
                            <div className="flex min-w-0 items-center justify-between gap-3">
                              <div className="appointment-patient-scroll inline-flex h-9 min-w-0 flex-1 items-center justify-start gap-1 overflow-x-auto overflow-y-hidden rounded-full bg-muted/50 p-1">
                                {selectedPetIds.map((petId) => {
                                  const pet = ownerPets.find((p) => p.id === petId) ?? null;
                                  const isActive = petId === activePetId;
                                  return (
                                    <button
                                      key={petId}
                                      type="button"
                                      onClick={() => setActivePetId(petId)}
                                      className={cn(
                                        "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium transition-all",
                                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ",
                                        isActive
                                          ? "bg-primary text-primary-foreground shadow-sm"
                                          : "text-muted-foreground hover:bg-secondary hover:text-primary",
                                      )}
                                    >
                                      <span className="max-w-[180px] truncate">{pet?.name ?? t("calendar:appointmentForm.patient")}</span>
                                    </button>
                                  );
                                })}
                                {hasMorePatientsToAdd ? (
                                  <button
                                    type="button"
                                    onClick={() => setIsWhoEditing(true)}
                                    className={cn(
                                      "w-7 !h-7",
                                      "inline-flex items-center justify-center whitespace-nowrap rounded-full ml-1 px-1 py-1 text-xs font-medium transition-all",
                                      "text-muted-foreground hover:bg-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                                    )}
                                    aria-label={t("calendar:appointmentForm.addAnotherPatient")}
                                  >
                                    <PlusCircle className="h-3.5 w-3.5" />
                                  </button>
                                ) : null}
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 rounded-full shrink-0"
                                onClick={() => {
                                  setIsWhoEditing(true);
                                  setSelectedOwnerSnapshot(null);
                                  form.setValue("owner_id", "");
                                  form.setValue("pet_ids", []);
                                  form.clearErrors("pet_ids");
                                  setActivePetId(null);
                                  setOwnerDisplayValue("");
                                }}
                              >
                                {t("calendar:appointmentForm.change")}
                              </Button>
                            </div>

                            <PatientSummaryInline
                              owner={selectedOwnerSnapshot}
                              petId={activePetId ?? undefined}
                              fallbackPet={ownerPets.find((pet) => pet.id === activePetId) ?? null}
                              onClose={onClose}
                            />
                          </div>
                        ) : (
                          <>
                            <FormField
                              control={form.control}
                              name="owner_id"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    {selectedPetIds.length > 0 ? t("calendar:appointmentForm.initialPatient") : t("calendar:appointmentForm.patient")} <span className="text-destructive">*</span>
                                  </FormLabel>
                                  <FormControl>
                                    <Autocomplete
                                      options={ownerOptions}
                                      value={ownerDisplayValue}
                                      popoverPortalled={false}
                                      onChange={(value) => {
                                        setOwnerDisplayValue(value);
                                        if (!value || value.trim() === "") {
                                          field.onChange("");
                                          form.setValue("pet_ids", []);
                                          form.clearErrors("pet_ids");
                                          setActivePetId(null);
                                          setSelectedOwnerSnapshot(null);
                                          setIsWhoEditing(true);
                                        }
                                      }}
                                      onSelect={(option) => {
                                        const matchedPatient = patientSearchOptions.find((item) => item.value === option.value);
                                        if (!matchedPatient) return;

                                        const currentPetIds = form.getValues("pet_ids");
                                        const nextPetIds = buildNextPetIdsForOwnerSelection(
                                          matchedPatient.ownerId,
                                          matchedPatient.petId,
                                          currentPetIds,
                                        );

                                        setOwnerDisplayValue(matchedPatient.petName);
                                        field.onChange(matchedPatient.ownerId);
                                        form.setValue("pet_ids", nextPetIds, {
                                          shouldDirty: true,
                                          shouldValidate: true,
                                        });
                                        setSelectedPetDeathSnapshot((prev) => ({
                                          ...prev,
                                          [matchedPatient.petId]: {
                                            statusName: matchedPatient.petStatusName,
                                            deathDate: matchedPatient.petDeathDate,
                                          },
                                        }));
                                        form.clearErrors("pet_ids");
                                        setActivePetId(matchedPatient.petId);
                                        setSelectedOwnerSnapshot(null);
                                        setIsWhoEditing(false);
                                      }}
                                      selectedValue={selectedPatientAutocompleteValue}
                                      placeholder={t("calendar:appointmentForm.searchPatient")}
                                      emptyMessage={
                                        showOwnerSearchLoading ? (
                                          <div className="py-6 text-center">
                                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto" />
                                            <p className="text-sm text-muted-foreground mt-2">{t("calendar:appointmentForm.searching")}</p>
                                          </div>
                                        ) : (
                                          <div className="py-6 text-center px-4">
                                            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                                              <CustomIcons name="user" className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <p className="mt-3 text-sm font-semibold text-foreground">
                                              {ownerDisplayValue.trim().length === 0
                                                ? t("calendar:appointmentForm.searchPatientPrompt")
                                                : t("calendar:appointmentForm.noPatientFound")}
                                            </p>
                                            <p className="mt-1 text-xs text-muted-foreground">
                                              {ownerDisplayValue.trim().length === 0
                                                ? t("calendar:appointmentForm.searchPatientHint")
                                                : t("calendar:appointmentForm.searchPatientRetry")}
                                            </p>
                                          </div>
                                        )
                                      }
                                      footer={
                                        ownerDisplayValue.trim().length > 0 && !ownerHasExactMatch ? (
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="w-full justify-start text-primary hover:bg-accent"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              openOwnerAndPetRegister();
                                            }}
                                          >
                                            <PlusCircle className="h-4 w-4" />
                                            {t("calendar:appointmentForm.registerFullPatient")}
                                          </Button>
                                        ) : null
                                      }
                                      renderOption={(option, isSelected) => {
                                        const patient = patientSearchOptions.find((item) => item.value === option.value);
                                        if (!patient) {
                                          return (
                                            <>
                                              <Check
                                                className={cn(
                                                  "mr-2 h-4 w-4 shrink-0",
                                                  isSelected ? "opacity-100" : "opacity-0",
                                                )}
                                              />
                                              <span className="min-w-0 max-w-[200px] truncate md:max-w-[400px] flex-1 text-left">
                                                {option.label}
                                              </span>
                                            </>
                                          );
                                        }
                                        const speciesAndBreed = [patient.petSpeciesName, patient.petBreedName]
                                          .filter(Boolean)
                                          .join(" • ");

                                        return (
                                          <>
                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4 shrink-0 mt-0.5",
                                                isSelected ? "opacity-100" : "opacity-0",
                                              )}
                                            />
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                  <div className="flex items-center gap-2">
                                                    <p className="font-medium leading-none truncate">{patient.petName}</p>
                                                    {isPetDeceased({ status: { name: patient.petStatusName }, death_date: patient.petDeathDate }) ? (
                                                      <Badge variant="outline" className="h-5 shrink-0 text-[10px]">
                                                        {t("calendar:appointmentForm.deceasedBadge")}
                                                      </Badge>
                                                    ) : null}
                                                  </div>
                                                  <p className="mt-1 text-xs text-muted-foreground truncate">
                                                    {t("calendar:quickStart.owner")}: {patient.ownerName}
                                                  </p>
                                                </div>
                                                {patient.petRecordNumber ? (
                                                  <Badge variant="secondary" className="h-5 shrink-0 text-[10px]">
                                                    {t("calendar:appointmentForm.record", { number: patient.petRecordNumber })}
                                                  </Badge>
                                                ) : null}
                                              </div>
                                              <div className="mt-1 space-y-0.5">
                                                {speciesAndBreed ? (
                                                  <p className="text-xs text-muted-foreground truncate">
                                                    {speciesAndBreed}
                                                  </p>
                                                ) : null}
                                                {patient.ownerPrimaryPhone ? (
                                                  <p className="text-xs text-muted-foreground truncate">
                                                    {patient.ownerPrimaryPhone}
                                                  </p>
                                                ) : null}
                                                {patient.ownerPrimaryEmail ? (
                                                  <p className="text-xs text-muted-foreground">
                                                    {patient.ownerPrimaryEmail}
                                                  </p>
                                                ) : null}
                                              </div>
                                            </div>
                                          </>
                                        );
                                      }}
                                      disableLocalFilter
                                    />
                                  </FormControl>
                                  {selectedPetIds.length > 0 ? (
                                    <p className="text-xs text-muted-foreground">
                                      {t("calendar:appointmentForm.firstPatientHint")}
                                    </p>
                                  ) : null}
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {selectedOwnerId ? (
                              <FormField
                                control={form.control}
                                name="pet_ids"
                                render={({ field }) => (
                                  <FormItem className="flex flex-col">
                                    <FormLabel>{t("calendar:appointmentForm.morePatients")}</FormLabel>
                                    {additionalOwnerPets.length === 0 ? (
                                      <div className="space-y-3 rounded-md border border-dashed p-3">
                                        <p className="text-sm text-muted-foreground">
                                          {t("calendar:appointmentForm.noOtherPatients")}
                                        </p>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="ghost"
                                          className="w-full justify-start px-0 text-primary hover:bg-accent"
                                          onClick={openPetOnlyRegister}
                                        >
                                          <PlusCircle className="h-4 w-4" />
                                          {t("calendar:appointmentForm.registerFullPatient")}
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="space-y-2 rounded-md border border-border/60 p-3">
                                        {additionalOwnerPets.map((pet) => {
                                          const checked = (field.value ?? []).includes(pet.id);
                                          return (
                                            <label
                                              key={pet.id}
                                              className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/50"
                                            >
                                              <Checkbox
                                                checked={checked}
                                                onCheckedChange={(nextChecked) => {
                                                  const isChecked = Boolean(nextChecked);
                                                  const next = isChecked
                                                    ? Array.from(new Set([...(field.value ?? []), pet.id]))
                                                    : (field.value ?? []).filter((id) => id !== pet.id);
                                                  field.onChange(next);
                                                }}
                                              />
                                              <span className="text-sm font-medium">{pet.name}</span>
                                            </label>
                                          );
                                        })}
                                      </div>
                                    )}
                                    <FormMessage />
                                    <div className="flex justify-end">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="secondary"
                                        className="mt-2"
                                        onClick={() => {
                                          if ((field.value ?? []).length === 0) return;
                                          setIsWhoEditing(false);
                                          setActivePetId(primarySelectedPetId ?? (field.value ?? [])[0] ?? null);
                                        }}
                                        disabled={(field.value ?? []).length === 0}
                                      >
                                        {t("calendar:appointmentForm.continue")}
                                      </Button>
                                    </div>
                                  </FormItem>
                                )}
                              />
                            ) : null}
                          </>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
                {mobileLayout && mode === "edit" && (
                  <section className="mt-4 rounded-[18px] border border-destructive/30 bg-card p-4 shadow-sm">
                    <Button
                      type="button"
                      variant="destructive"
                      className="h-[52px] w-full rounded-[15px] text-base gap-2"
                      onClick={async () => {
                        if (initialAppointment?.recurrence_id) {
                          try {
                            const hasFollowing = await hasFollowingRecurrenceOccurrences();
                            if (hasFollowing) {
                              setIsDeleteScopeDialogOpen(true);
                              return;
                            }
                          } catch (error) {
                            toast({
                              title: t("calendar:appointmentForm.applyRecurrenceTitle"),
                              description: error instanceof Error ? error.message : t("calendar:quickStart.tryAgain"),
                              variant: "destructive",
                            });
                            return;
                          }
                          setIsDeleteDialogOpen(true);
                          return;
                        }
                        setIsDeleteDialogOpen(true);
                      }}
                      disabled={
                        createAppointment.isPending ||
                        createRecurringAppointments.isPending ||
                        updateAppointment.isPending ||
                        isDeleting
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                      {t("calendar:appointmentForm.deleteAppointment")}
                    </Button>
                  </section>
                )}
              </div>
              <div className={`shrink-0 border-t border-border/60 ${mobileLayout ? "bg-background-app px-4 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]" : "bg-white px-6 py-4"}`}>
                <div className={`flex gap-2 ${mobileLayout ? "flex-row items-center" : "flex-col sm:flex-row sm:items-center sm:justify-between"}`}>
                  {!mobileLayout && mode === "edit" ? (
                    <Button
                      type="button"
                      variant="destructive"
                      className="gap-2"
                      onClick={async () => {
                        if (initialAppointment?.recurrence_id) {
                          try {
                            const hasFollowing = await hasFollowingRecurrenceOccurrences();
                            if (hasFollowing) {
                              setIsDeleteScopeDialogOpen(true);
                              return;
                            }
                          } catch (error) {
                            toast({
                              title: t("calendar:appointmentForm.applyRecurrenceTitle"),
                              description: error instanceof Error ? error.message : t("calendar:quickStart.tryAgain"),
                              variant: "destructive",
                            });
                            return;
                          }
                          setIsDeleteDialogOpen(true);
                          return;
                        }
                        setIsDeleteDialogOpen(true);
                      }}
                      disabled={
                        createAppointment.isPending ||
                        createRecurringAppointments.isPending ||
                        updateAppointment.isPending ||
                        (mode !== "edit" && replaceAppointmentItems.isPending) ||
                        isDeleting
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                      {t("calendar:appointmentForm.deleteAppointment")}
                    </Button>
                  ) : null}

                  <div className={`flex gap-2 ${mobileLayout ? "flex-1 flex-row" : "flex-col sm:flex-row sm:justify-end sm:ml-auto"}`}>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={
                        createAppointment.isPending ||
                        createRecurringAppointments.isPending ||
                        updateAppointment.isPending ||
                        (mode !== "edit" && replaceAppointmentItems.isPending) ||
                        deleteAppointment.isPending
                      }
                      className={mobileLayout ? "h-[52px] w-[100px] shrink-0 rounded-[15px] text-base" : ""}
                    >
                      {t("common:cancel")}
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        createAppointment.isPending ||
                        createRecurringAppointments.isPending ||
                        updateAppointment.isPending ||
                        (mode !== "edit" && replaceAppointmentItems.isPending)
                      }
                      className={mobileLayout ? "h-[52px] flex-1 rounded-[15px] text-base shadow-lg shadow-primary/30" : ""}
                    >
                      {mode === "edit"
                        ? updateAppointment.isPending
                          ? t("calendar:appointmentForm.saving")
                          : t("calendar:appointmentForm.saveChanges")
                        : (createAppointment.isPending || createRecurringAppointments.isPending)
                          ? t("calendar:appointmentForm.scheduling")
                          : t("calendar:appointmentForm.creating")}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </Form>
        ) : (
          <div className="p-6">
            <div className="space-y-3 rounded-md border border-dashed border-border/60 bg-muted/40 p-4">
              <p className="text-sm text-muted-foreground">
                {t("calendar:appointmentForm.noClinicSelected")}
              </p>
            </div>
          </div>
        )
        }
      </div>
      <QuickRegisterPatientDialog
        open={isRegisterDialogOpen}
        onOpenChange={setIsRegisterDialogOpen}
        mode={registerMode}
        initialOwnerId={registerMode === "pet_only" ? selectedOwnerId : undefined}
        initialOwnerName={registerMode === "pet_only" ? (selectedOwnerSnapshot?.name ?? ownerDisplayValue) : undefined}
        onCompleted={async ({ ownerId, ownerName, petId, petName }) => {
          const currentPetIds = form.getValues("pet_ids");
          const nextPetIds = buildNextPetIdsForOwnerSelection(ownerId, petId, currentPetIds);

          form.setValue("owner_id", ownerId, { shouldDirty: true, shouldValidate: true });
          form.setValue("pet_ids", nextPetIds, { shouldDirty: true, shouldValidate: true });
          form.clearErrors("owner_id");
          form.clearErrors("pet_ids");
          setSelectedOwnerSnapshot(null);
          setOwnerDisplayValue(ownerName);
          setActivePetId(petId);
          setIsWhoEditing(false);
          await queryClient.invalidateQueries({ queryKey: ["owners"] });
          await queryClient.invalidateQueries({ queryKey: ["pets"] });
          toast({
            title: t("calendar:appointmentForm.registrationDoneTitle"),
            description: t("calendar:appointmentForm.registrationDoneDescription", { ownerName, petName }),
          });
        }}
      />

      <AlertDialog open={isDeceasedSubmitDialogOpen} onOpenChange={setIsDeceasedSubmitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendar:appointmentForm.deceasedSubmitTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendar:appointmentForm.deceasedSubmitDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDeceasedSubmitData(null)}>
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const pending = pendingDeceasedSubmitData;
                setPendingDeceasedSubmitData(null);
                setIsDeceasedSubmitDialogOpen(false);
                if (pending) {
                  void executeSubmit(pending);
                }
              }}
            >
              {t("calendar:appointmentForm.continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendar:appointmentForm.confirmDeletion")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendar:appointmentForm.confirmDeletionDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteAppointment("single")}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t("calendar:appointmentForm.deleting") : t("calendar:appointmentForm.deleteAppointment")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isDeleteScopeDialogOpen} onOpenChange={setIsDeleteScopeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendar:appointmentForm.deleteRecurringTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendar:appointmentForm.deleteRecurringDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteAppointment("single")}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("calendar:appointmentForm.onlyThis")}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => handleDeleteAppointment("future")}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("calendar:appointmentForm.thisAndNext")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isRecurrenceScopeDialogOpen}
        onOpenChange={setIsRecurrenceScopeDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendar:appointmentForm.applyRecurrenceTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendar:appointmentForm.applyRecurrenceDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isApplyingRecurrenceAction}
              onClick={() => {
                setPendingRecurringSubmitData(null);
                setPendingRecurrenceOverflowCount(0);
              }}
            >
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!pendingRecurringSubmitData) return;
                setIsApplyingRecurrenceAction(true);
                try {
                  await executeSubmit(pendingRecurringSubmitData, "single");
                  setIsRecurrenceScopeDialogOpen(false);
                  setPendingRecurringSubmitData(null);
                } finally {
                  setIsApplyingRecurrenceAction(false);
                }
              }}
              disabled={isApplyingRecurrenceAction}
            >
              {isApplyingRecurrenceAction ? t("calendar:appointmentForm.applying") : t("calendar:appointmentForm.onlyThis")}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={async () => {
                if (!pendingRecurringSubmitData) return;
                setIsApplyingRecurrenceAction(true);
                try {
                  // O overflow só existe quando o PADRÃO de recorrência muda
                  // (dia, frequência, intervalo, término). Numa troca só de
                  // horário, as ocorrências são ajustadas no lugar (Caminho 1),
                  // então não há ocorrência "fora da regra" para remover.
                  const overflowCount = hasRecurringRuleChanges(pendingRecurringSubmitData)
                    ? await getFutureOverflowCount(pendingRecurringSubmitData)
                    : 0;
                  if (overflowCount > 0) {
                    setIsRecurrenceScopeDialogOpen(false);
                    setPendingRecurrenceOverflowCount(overflowCount);
                    setIsRecurrenceOverflowDialogOpen(true);
                    return;
                  }
                  await executeSubmit(pendingRecurringSubmitData, "future", "keep");
                  setIsRecurrenceScopeDialogOpen(false);
                  setPendingRecurringSubmitData(null);
                } finally {
                  setIsApplyingRecurrenceAction(false);
                }
              }}
              disabled={isApplyingRecurrenceAction}
            >
              {isApplyingRecurrenceAction ? t("calendar:appointmentForm.applying") : t("calendar:appointmentForm.thisAndNext")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={isRecurrenceOverflowDialogOpen}
        onOpenChange={setIsRecurrenceOverflowDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("calendar:appointmentForm.overflowTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("calendar:appointmentForm.overflowDescription", { count: pendingRecurrenceOverflowCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isApplyingRecurrenceAction}
              onClick={() => {
                setPendingRecurrenceOverflowCount(0);
                setPendingRecurringSubmitData(null);
              }}
            >
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!pendingRecurringSubmitData) return;
                setIsApplyingRecurrenceAction(true);
                try {
                  await executeSubmit(pendingRecurringSubmitData, "future", "keep");
                  setIsRecurrenceOverflowDialogOpen(false);
                  setPendingRecurrenceOverflowCount(0);
                  setPendingRecurringSubmitData(null);
                } finally {
                  setIsApplyingRecurrenceAction(false);
                }
              }}
              disabled={isApplyingRecurrenceAction}
            >
              {isApplyingRecurrenceAction ? t("calendar:appointmentForm.applying") : t("calendar:appointmentForm.keep")}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={async () => {
                if (!pendingRecurringSubmitData) return;
                setIsApplyingRecurrenceAction(true);
                try {
                  await executeSubmit(pendingRecurringSubmitData, "future", "delete");
                  setIsRecurrenceOverflowDialogOpen(false);
                  setPendingRecurrenceOverflowCount(0);
                  setPendingRecurringSubmitData(null);
                } finally {
                  setIsApplyingRecurrenceAction(false);
                }
              }}
              disabled={isApplyingRecurrenceAction}
            >
              {isApplyingRecurrenceAction ? t("calendar:appointmentForm.applying") : t("common:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

type PatientSummaryInlineProps = {
  owner: OwnerWithContacts | null;
  petId: string | undefined;
  fallbackPet: PetWithOwner | null;
  onClose?: () => void;
};

const resolvePhotoPath = (
  photo?: { file_path: string } | { file_path: string }[] | null,
): string | null => {
  if (!photo) return null;
  if (Array.isArray(photo)) return photo[0]?.file_path ?? null;
  return photo.file_path ?? null;
};

function PatientSummaryInline({ owner, petId, fallbackPet, onClose }: PatientSummaryInlineProps) {
  const { data: petSummary, isLoading: isLoadingPetSummary } = usePetSummary(petId);

  const pet = petSummary ?? fallbackPet;
  const petPhoto = resolvePhotoPath(pet?.photo ?? null);
  const ownerNotes = (
    (owner as unknown as { notes?: string | null } | null | undefined)?.notes?.trim?.() ?? ""
  );
  const petNotes = (pet?.notes as string | null | undefined)?.trim?.() ? (pet?.notes as string) : "";
  const ownerName = owner?.name?.trim() ?? "";

  const allergies = petSummary?.allergies ?? [];
  const temperaments = petSummary?.temperaments ?? [];

  return (
    <div className="min-w-0 space-y-4">
      <div className="space-y-4">
        {!petId ? (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            Selecione um paciente para ver alergias, temperamento e observações.
          </div>
        ) : isLoadingPetSummary ? (
          <div className="rounded-lg border border-dashed p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Carregando resumo do paciente...
            </div>
          </div>
        ) : !pet ? (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            Não foi possível carregar os dados do paciente.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                {petPhoto ? (
                  <AvatarImage src={petPhoto} alt={pet.name} />
                ) : (
                  <AvatarFallback className="bg-muted/50 text-muted-foreground">
                    <CustomIcons name="paws" className="h-5 w-5 text-current" />
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/patients/pets/${petId}`}
                  onClick={() => onClose?.()}
                  className="block truncate text-base font-semibold text-primary hover:underline"
                >
                  {pet.name}
                </Link>
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  {[pet.species?.name, pet.breed?.name, pet.birth_date ? formatPetAge(pet.birth_date) : null]
                    .filter(Boolean)
                    .join(" • ")}
                </p>
                {owner?.id && ownerName ? (
                  <p className="mt-1 truncate text-sm text-muted-foreground">
                    Responsável:{" "}
                    <Link
                      to={`/patients/owners/${owner.id}`}
                      onClick={() => onClose?.()}
                      className="font-medium text-primary hover:underline"
                    >
                      {ownerName}
                    </Link>
                  </p>
                ) : null}
              </div>
            </div>

            <Separator />

            <div className="grid gap-4">
              <div>
                <p className="text-xs font-semibold text-muted-foreground">Alergias</p>
                <div className="mt-2">
                  {allergies.length ? (
                    <div className="flex flex-wrap gap-2">
                      {allergies.map((item) => (
                        <Badge key={item.id} variant="default">
                          {item.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhuma alergia registrada.</p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-muted-foreground">Temperamento</p>
                <div className="mt-2">
                  {temperaments.length ? (
                    <div className="flex flex-wrap gap-2">
                      {temperaments.map((item) => (
                        <Badge key={item.id} variant="secondary">
                          {item.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhum temperamento registrado.</p>
                  )}
                </div>
              </div>
            </div>

            {(petNotes || ownerNotes) ? (
              <>
                <Separator />
                <div className="space-y-3">
                  {petNotes ? (
                    <div className="rounded-md border bg-muted/30 p-3">
                      <p className="text-xs font-semibold text-muted-foreground">Observações do paciente</p>
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">{petNotes}</p>
                    </div>
                  ) : null}
                  {ownerNotes ? (
                    <div className="rounded-md border bg-muted/30 p-3">
                      <p className="text-xs font-semibold text-muted-foreground">Observações do responsável</p>
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">{ownerNotes}</p>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
